import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Select,
  InputNumber,
  Input,
  Menu,
  Modal,
  notification,
  Popover,
  Tag,
  Table,
  Tooltip,
  Dropdown,
} from 'antd'
import styled, { css } from 'styled-components/macro'
import { antdTableHorizontalScroll } from 'src/components/styled/horizontalScroll'
import { Prompt, useHistory } from 'react-router-dom'
import {
  GoldenFilled,
  LoadingOutlined,
  InfoCircleOutlined,
  CommentOutlined,
  DownOutlined,
  EditOutlined,
} from '@ant-design/icons'
import AddProductModal from './AddProductModal'
import ProductConversionModal from './ProductConversionModal'
import AddSoldRecipesModal from './AddSoldRecipesModal'
import AddWastedRecipesModal, {
  WastedRecipesMenuLabel,
} from './AddWastedRecipesModal'
import {
  StockWastageColumnHint,
  StockWastageQuantity,
  StockWastageRecordLink,
  stockLineNotInCatalogue,
  stockLineRecordProductId,
  stockWastageIsFromTracker,
} from 'src/components/stock/StockWastageCell'
import {
  WASTAGE_COPY,
  wastageBusinessDateForStockTake,
  wastageEntryDateLabel,
  wastageLondonToday,
} from 'src/constants/wastage'
import {
  stockCountEnteredFlags,
  stockInStockWasEntered,
  stockLineMoneyLabel,
  stockSoldWasEntered,
  wasteExceedsStockOnHand,
} from 'src/utils/stockOnHand'
import { recordWastedRecipes } from 'src/utils/wastedRecipeEntries'
import { useDispatch, useSelector } from 'react-redux'
import {
  addVenueProductLineToStockTake,
  addOrderToParToBasket,
  fetchOrderToParSuggestions,
  fetchStockTakeProducts,
  fetchStockTakes,
  saveAllChanges,
  updateStockTake,
} from '../../features/StockSlice'
import { editVenueProduct } from 'src/features/VenueProductsSlice'
import { fetchAllRecipes } from 'src/features/RecipeBuilderSlice'
import dayjs from 'dayjs'
import Loading from '../../components/styled/Loading'
import { fetchVenueProducts } from '../../features/VenueProductsSlice'
import { VAT_PERCENTAGE, VAT_RATE } from '../../config'
import StockTakeCSV from './StockTakeCSV'
import Papa from 'papaparse'
import * as XLSX from 'xlsx'
import wordCaps from '../../utils/wordCaps'
import { SuccessNotification } from 'src/notifications/SuccessNotification'
import { calculateIngredientUsage } from 'src/utils/recipeIngredientCalculator'
import { API } from 'src/utils/interceptor'
import {
  formatGbp,
  formatStockTakeExpectedQtyDisplay,
  formatStockTakeVarianceDisplay,
} from 'src/utils/formatGbp'
import { StockTakeInputNumber } from 'src/utils/stockTakeStepperAria'
import useStockRoleCapabilities from 'src/hooks/useStockRoleCapabilities'
import { filterStockTableColumns } from 'src/utils/permissions'
import {
  getEffectiveStockUnitPrice,
  getLineStockQuantityValue,
  getRecipeIdString,
  isRecipeStockProduct,
} from 'src/utils/stockTakeRecipeProduct'
import {
  getConversionOptionsForStockProduct,
  mapConversionRecordsToOptions,
} from 'src/utils/stockTakeConversions'
import {
  STOCK_TAKE_UNCOUNTED_PLACEHOLDER,
  countedStockInputValue,
  markStockRowsCounted,
  shouldShowDuplicatePackWarning,
} from 'src/utils/stockTakeCountFlag'
import {
  STOCK_TAKE_BELOW_PAR_EMPTY,
  STOCK_TAKE_BELOW_PAR_FILTER_LABEL,
  sortBelowParCountedLines,
  summarizeBelowParSoFar,
} from 'src/utils/stockTakeBelowParSoFar'
import {
  applyStockTakeParEdits,
  canEditStockTakeLinePar,
  collectStockTakeParSaves,
  displayParLevelForStockLine,
  stockTakeParUnavailableCopy,
  dropPersistedParEditKeys,
  STOCK_TAKE_COUNTS_SAVED_PARS_FAILED,
  venueProductForStockTakeLine,
} from 'src/utils/stockTakeLinePar'
import {
  stockTakeDraftIsDirty,
  stockTakeLineDiffersFromSaved,
} from 'src/utils/stockTakeDraftDirty'
import {
  formatMissingParStripCopy,
  listMissingParProductsForSuppliers,
} from 'src/utils/stockTakeMissingParStrip'
import {
  patchDummyStockTakeNotes,
  shouldPersistDummyStockTakeNoteClear,
  uniqueStockTakeSupplierIds,
} from 'src/utils/stockTakeCreateForm'
import {
  StockPrimaryButton,
  StockQuietButton,
} from 'src/components/stock/StockPageChrome'
import {
  displayStockTakeReasonForCount,
  formatStockTakeDateRange,
  getStockTakePageHeading,
  isSupplierScopedStockTake,
} from 'src/utils/stockTakePartialCard'
import {
  formatOtherPacksToggleLabel,
  listVisibleStockTakePackLines,
} from 'src/utils/stockTakePreferredPack'
import {
  ORDER_TO_PAR_ADDED_TO_BASKET,
  ORDER_TO_PAR_ALREADY_IN_BASKET,
  ORDER_TO_PAR_COUNT_HINT,
  ORDER_TO_PAR_REVIEW_HINT,
  ORDER_TO_PAR_SET_PAR_CTA,
  STOCK_TAKE_PAR_FOCUS_ROW_CLASS,
  focusStockTakeParInput,
  stockTakeParInputTestId,
  formatOrderToParLockSummary,
  formatOrderToParCoverageCopy,
  formatOrderToParPackCopy,
  formatOrderToParAskOllieChip,
  formatOrderToParStripCopy,
  ORDER_TO_PAR_STRIP_REVIEW,
  orderToParEmptyCopy,
  ORDER_TO_PAR_SAVE_BEFORE_BASKET,
  ORDER_TO_PAR_PREVIEW_TOTAL_HINT,
  ORDER_TO_PAR_INACTIVE_SKIPPED_HINT,
  coverageMapsFromOrderToParItems,
  previewOrderToParFromStockLines,
  canAddOrderToParToBasket as orderToParBasketIsReady,
  orderToParAddToBasketLabel,
  orderToParShowsSetParCta,
  orderToParSuggestionKey,
  pickFirstCountedLineWithoutPar,
  pickOrderToParEmptyState,
  reviewOrderToParList,
  buildOrderToParBasketReviewLines,
  summarizeOrderToParReview,
  countInactiveShoppingListSkipped,
} from 'src/utils/stockTakeOrderToParReview'
import {
  STOCK_TAKE_ANALYTICS_EVENTS,
  trackStockTakeAnalytics,
} from 'src/utils/stockTakeAnalytics'
import StockTakeAskOllie from './StockTakeAskOllie'
import { useSuppressOllieFab } from 'src/components/ollie/OllieAssistantContext'
import {
  countVenueProductsWithParLevel,
  formatParLevelUsage,
  MAX_PAR_LEVEL,
  stockTakeParColumnHelp,
  stockTakeParInputHelp,
  PAR_LEVEL_LIMIT_ERROR_CODE,
  parseParLevelValue,
} from 'src/utils/parLevelPaywall'
import {
  trackParLevelPaywallTriggered,
  trackParLevelSaveIfChanged,
} from 'src/utils/parLevelAnalytics'
import { notifyParLevelPaywallSales } from 'src/utils/parLevelPaywallNotify'
import ParLevelUpgradeModalContent from 'src/utils/ParLevelUpgradeModalContent'
import {
  filterStockTakeCountModeColumns,
  reorderStockTakeTableColumns,
  shouldDefaultStockTakeCountMode,
  shouldShowStockTakeConversionPencil,
  shouldShowStockTakeConversionSelect,
  sumStockTakeColumnScrollX,
  STOCK_TAKE_SUPPLIER_COLUMN_WIDTH,
  STOCK_TAKE_TITLE_COLUMN_WIDTH,
  STOCK_TAKE_TYPE_COLUMN_WIDTH,
} from 'src/utils/stockTakeTableColumnOrder'
import {
  formatStockTakeCountModePackText,
  formatStockTakeLinePackLabel,
  formatStockTakeUomSelectBaseLabel,
  unwrapStockTakeOriginalUom,
} from 'src/utils/stockTakeLineTitle'
import {
  currentStockTakeUserName,
  displayStockTakeLastEditedBy,
  formatStockTakeCountProgress,
  hasSavedCountedStockTakeLine,
  stockTakeHasPersistedCount,
  stockTakeSavedAtFromRecord,
} from 'src/utils/stockTakeHud'
import {
  STOCK_TAKE_UNSAVED_LEAVE_MESSAGE,
  commitStockTakeInProgressCells,
  shouldPromptStockTakeLeave,
} from 'src/utils/stockTakeLeaveGuard'
import {
  STOCK_TAKE_NOT_FOUND_COPY,
  stockTakeDetailState,
  stockTakeDisplayedSavedAt,
  stockTakeLastEditedBy,
  stockTakeLastSavedLabel,
  stockTakeLockLabel,
  stockTakeSavePillLabel,
  shouldShowStockTakeLockTime,
  stockTakeVisibleMetaRows,
} from 'src/utils/stockTakeDetailState'
import {
  buildReviewBeforeLockStats,
  confirmStockTakeLockChange,
} from 'src/utils/stockTakeLockConfirm'
import { canUseStockTakeVarianceEmail } from 'src/utils/stockTakePlanGate'

const STOCK_TAKE_FILE_ACCEPT = '.csv,.CSV,.xlsx,.XLSX,.xls,.XLS'

const cellToString = value => {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

const getStockTakeFileExtension = file => {
  const name = file?.name || ''
  const dotIndex = name.lastIndexOf('.')
  if (dotIndex === -1) return ''
  return name.slice(dotIndex + 1).toLowerCase()
}

const parseStockTakeCsvFile = file =>
  new Promise((resolve, reject) => {
    Papa.parse(file, {
      headers: false,
      skipEmptyLines: false,
      complete: res => resolve(res?.data || []),
      error: reject,
    })
  })

const parseStockTakeExcelFile = async file => {
  const arrayBuffer = await file.arrayBuffer()
  const workbook = XLSX.read(arrayBuffer, { type: 'array' })
  const sheetName = workbook.SheetNames[0]
  if (!sheetName) {
    throw new Error('NO_SHEETS')
  }
  const sheet = workbook.Sheets[sheetName]
  return XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' })
}

const processStockTakeRows = async (
  rows,
  { stockTakeId, venueId, dispatch },
) => {
  // Spreadsheet structure:
  // Row 0: Title ("Stock take for...")
  // Row 1: Empty
  // Row 2: Headers
  // Row 3+: Data rows

  if (!rows || rows.length < 3) {
    notification.error({
      message: 'Invalid file format',
      description:
        'File must have at least 3 rows (title, empty, headers). Please download a fresh template and try again.',
      placement: 'topRight',
    })
    return
  }

  const headers = (rows[7] || []).map(h => cellToString(h))

  const requiredHeaders = ['id', 'Title', 'In stock', 'Wastage']
  const missingHeaders = requiredHeaders.filter(
    reqHeader =>
      !headers.some(h => h.toLowerCase() === reqHeader.toLowerCase()),
  )

  if (missingHeaders.length > 0) {
    notification.error({
      message: 'Missing required columns',
      description: `File is missing required columns: ${missingHeaders.join(
        ', ',
      )}. Please download a fresh template and try again.`,
      placement: 'topRight',
    })
    return
  }

  const getColumnIndex = columnName =>
    headers.findIndex(h => h.toLowerCase() === columnName.toLowerCase())

  const idIndex = getColumnIndex('id')
  const titleIndex = getColumnIndex('Title')
  const typeIndex = getColumnIndex('Type')
  const sizeIndex = getColumnIndex('Size')
  const supplierIndex = getColumnIndex('Supplier')
  const priceIndex = getColumnIndex('Price')
  const skuIndex = getColumnIndex('SKU')
  const prevQtyIndex = getColumnIndex('Prev quantity')
  const receivedIndex = getColumnIndex('Received')
  const soldIndex = getColumnIndex('Sold')
  const inStockIndex = getColumnIndex('In stock')
  const wastageIndex = getColumnIndex('Wastage')

  if (idIndex === -1 || titleIndex === -1 || inStockIndex === -1) {
    notification.error({
      message: 'Invalid file format',
      description:
        'File is missing critical columns (id, Title, or In stock). Please download a fresh template and try again.',
      placement: 'topRight',
    })
    return
  }

  const products = []
  const errors = []

  const safeParseNumber = (value, defaultValue = 0) => {
    if (value === null || value === undefined || value === '') {
      return defaultValue
    }
    const parsed = Number(value)
    if (isNaN(parsed) || !isFinite(parsed)) {
      return defaultValue
    }
    return parsed
  }

  for (let i = 8; i < rows.length; i++) {
    const row = rows[i]
    if (!row || row.length === 0) continue

    try {
      const product = {}

      product.id = cellToString(row[idIndex])
      product._id = product.id
      product.title = cellToString(row[titleIndex])
      product.type = cellToString(row[typeIndex])
      product.size = cellToString(row[sizeIndex])
      product.supplierTitle = cellToString(row[supplierIndex])
      product.price = safeParseNumber(row[priceIndex], 0)
      product.sku = cellToString(row[skuIndex])
      product.lastQuantity = safeParseNumber(row[prevQtyIndex], 0)
      product.qtySinceLastStockTake = safeParseNumber(row[receivedIndex], 0)

      const inStockCell = cellToString(row[inStockIndex]).trim()
      const soldCell =
        soldIndex !== -1 ? cellToString(row[soldIndex]).trim() : ''
      product.sold = soldCell === '' ? 0 : safeParseNumber(soldCell, 0)
      product.soldEntered = soldCell !== ''
      product.inStock = safeParseNumber(row[inStockIndex], 0)
      product.wastage = safeParseNumber(row[wastageIndex], 0)
      Object.assign(product, stockCountEnteredFlags(inStockCell !== ''))
      if (inStockCell === '') product.inStock = 0

      if (product.sold < 0) {
        errors.push(
          `Row ${i + 1}: Sold value cannot be negative, defaulting to 0`,
        )
        product.sold = 0
        product.soldEntered = false
      }
      if (product.inStock < 0) {
        errors.push(
          `Row ${i + 1}: In stock value cannot be negative, defaulting to 0`,
        )
        product.inStock = 0
        Object.assign(product, stockCountEnteredFlags(false))
      }
      if (product.wastage < 0) {
        errors.push(
          `Row ${i + 1}: Wastage value cannot be negative, defaulting to 0`,
        )
        product.wastage = 0
      }

      product.stockTake = stockTakeId

      if (product.title || product.id) {
        products.push(product)
      } else {
        errors.push(`Row ${i + 1}: Skipped - missing both id and title`)
      }
    } catch (rowError) {
      errors.push(`Row ${i + 1}: Error processing row - ${rowError.message}`)
      console.error(`Error processing stock take row ${i + 1}:`, rowError, row)
    }
  }

  if (products.length === 0) {
    notification.error({
      message: 'No valid products found',
      description:
        'File did not contain any valid product rows. Please check your file.',
      placement: 'topRight',
    })
    return false
  }

  const allChanges = {
    stockTakeId: stockTakeId,
    toAdd: [],
    toUpdate: markStockRowsCounted(products),
  }
  const saved = await dispatch(
    saveAllChanges(venueId, allChanges, 'UPLOAD_FILE'),
  )

  if (!saved) {
    notification.error({
      message: 'Upload failed',
      description: 'Could not save stock take data. Please try again.',
      placement: 'topRight',
    })
    return false
  }

  if (errors.length > 0 && errors.length <= 10) {
    notification.warning({
      message: 'Upload completed with warnings',
      description:
        errors.slice(0, 5).join('; ') + (errors.length > 5 ? '...' : ''),
      placement: 'topRight',
      duration: 8,
    })
  } else if (errors.length > 10) {
    notification.warning({
      message: 'Upload completed with warnings',
      description: `${errors.length} rows had issues. Check console for details.`,
      placement: 'topRight',
      duration: 8,
    })
  }

  if (errors.length === 0) {
    SuccessNotification({
      message: 'File uploaded successfully',
      description: `${products.length} product(s) updated.`,
    })
  }

  return true
}

const Stock = props => {
  const {
    match: {
      params: { stockTakeId, venueId },
    },
  } = props
  const history = useHistory()
  const venue = useSelector(state => state.venue?.venue)
  const currentPlan = venue?.usageLimits?.currentPlan || 'Free'
  const stockTake = useSelector(state => state.stockTakes.stockTakes).find(
    stockTake => stockTake._id === stockTakeId,
  )
  const [stockTakeLookupDone, setStockTakeLookupDone] = useState(false)
  const isPartialStockTake =
    String(stockTake?.countType || 'MAIN').toUpperCase() === 'PARTIAL'
  const defaultsToCountMode = shouldDefaultStockTakeCountMode(stockTake)
  const [showFullCountGrid, setShowFullCountGrid] = useState(false)
  const [showOtherPacks, setShowOtherPacks] = useState(false)
  const [belowParOnly, setBelowParOnly] = useState(false)
  const useCountMode = defaultsToCountMode && !showFullCountGrid
  const isStockTakeLocked = Boolean(stockTake?.isLocked)
  const [moreMenuOpen, setMoreMenuOpen] = useState(false)
  const [addProductModalVisible, setAddProductModalVisible] = useState(false)
  const [addProductResetCount, setAddProductResetCount] = useState(0)
  const [addSoldRecipesModalVisible, setAddSoldRecipesModalVisible] =
    useState(false)
  const [soldRecipes, setSoldRecipes] = useState(stockTake?.soldRecipes || [])
  const [addWastedRecipesModalVisible, setAddWastedRecipesModalVisible] =
    useState(false)
  const [conversionModalState, setConversionModalState] = useState({
    visible: false,
    product: null,
  })
  const [wastedRecipes, setWastedRecipes] = useState(
    stockTake?.wastedRecipes || [],
  )
  const [wastedRecipeUsage, setWastedRecipeUsage] = useState([])
  const [savingWastedRecipes, setSavingWastedRecipes] = useState(false)
  const [partialScopeProductKeys, setPartialScopeProductKeys] = useState([])
  const [partialGroupLabel, setPartialGroupLabel] = useState('')
  const [product, setProduct] = useState({
    inStock: 0,
    type: '',
    title: '',
    size: '',
    UoM: '',
    sku: '',
    supplierTitle: '',
    price: 0,
    lastQuantity: 0,
    OMSPrice: 0,
    wastage: 0,
    sold: 0,
    hasVat: false,
    stockTake: stockTakeId,
    qtySinceLastStockTake: 0,
    recipeId: null,
    recipePortionsPerBatch: null,
    recipePriceMode: 'Portion',
  })
  const isLoading = useSelector(state => state.stockTakes.isLoading)
  const stockTakeProducts = useSelector(
    state => state.stockTakes.stockTakeProducts,
  )
  const recipes = useSelector(state => state.recipes?.recipes || [])
  const [stockTakeProductsTypes, setProductsTypes] = useState([])
  const [products, setProducts] = useState([])
  const [parEdits, setParEdits] = useState({})
  const [addingMissingParLines, setAddingMissingParLines] = useState(false)
  const [orderToParRecommendations, setOrderToParRecommendations] = useState([])
  const [orderToParSkipped, setOrderToParSkipped] = useState([])
  const [orderToParLoaded, setOrderToParLoaded] = useState(false)
  const [orderToParRemovedKeys, setOrderToParRemovedKeys] = useState([])
  const [orderToParQtyByKey, setOrderToParQtyByKey] = useState({})
  const [addingOrderToParToBasket, setAddingOrderToParToBasket] =
    useState(false)
  const [orderToParAskOllie, setOrderToParAskOllie] = useState(null)
  useSuppressOllieFab({ orderToParOpen: true })
  const [stockTakeMetadata, setStockTakeMetadata] = useState({
    siteName: stockTake?.siteName || '',
    siteManager: stockTake?.siteManager || '',
    lineManager: stockTake?.lineManager || '',
    countedByName: stockTake?.countedByName || '',
    reasonForCount: stockTake?.reasonForCount || '',
    countNotes: stockTake?.countNotes || '',
  })
  const dispatch = useDispatch()
  const { canViewExpectedAndVariance, canUnlockStockTake } =
    useStockRoleCapabilities()
  const showVarianceEmail = canUseStockTakeVarianceEmail(currentPlan)
  const buttonLoading = useSelector(state => state.stockTakes.buttonLoading)
  const authAccount = useSelector(state => state.auth.account)
  const currentUserName = currentStockTakeUserName(authAccount)
  const hasAnyConvertedRows = products.some(product =>
    Boolean(product?.conversionId),
  )

  // Helper function to render column header with tooltip and icon
  const renderHeaderWithTooltip = (title, tooltipText) => (
    <Tooltip title={tooltipText}>
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {title}
        <InfoCircleOutlined style={{ fontSize: '12px', color: '#8c8c8c' }} />
      </span>
    </Tooltip>
  )

  // Helper function to format numbers with 2-3 decimals and remove trailing zeros
  const formatDecimal = (value, maxDecimals = 3) => {
    if (
      value === null ||
      value === undefined ||
      isNaN(value) ||
      !isFinite(value)
    ) {
      return 0
    }
    const num = Number(value)
    // Round to maxDecimals to handle floating-point errors
    const rounded =
      Math.round(num * Math.pow(10, maxDecimals)) / Math.pow(10, maxDecimals)
    // Convert to string and remove trailing zeros
    const str = rounded.toString()
    // If it's a whole number, return as-is
    if (str.indexOf('.') === -1) {
      return rounded
    }
    // Remove trailing zeros and trailing decimal point
    return parseFloat(str)
  }

  const normalizeScopeValue = value =>
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
  const normalizeStoredScopeKey = rawKey => {
    const key = String(rawKey || '')
      .trim()
      .toLowerCase()
    if (!key) return ''
    const parts = key.split('::')
    if (parts.length >= 5) {
      const [title, supplierTitle, sku, size, uom] = parts
      return [
        normalizeScopeValue(title),
        normalizeScopeValue(supplierTitle),
        normalizeScopeValue(sku).replace(/\s+/g, ''),
        normalizeScopeValue(size),
        normalizeScopeValue(uom),
      ].join('::')
    }
    return normalizeScopeValue(key)
  }
  const buildScopeKey = value =>
    [
      normalizeScopeValue(value?.title),
      normalizeScopeValue(value?.supplierTitle),
      normalizeScopeValue(value?.sku).replace(/\s+/g, ''),
      normalizeScopeValue(value?.size),
      normalizeScopeValue(value?.UoM),
    ].join('::')
  const hasPartialScopeRestriction =
    isPartialStockTake && partialScopeProductKeys.length > 0

  const getSafeConversionFactor = product => {
    const factor = Number(product?.conversionFactor)
    return Number.isFinite(factor) && factor > 0 ? factor : 1
  }
  const hasActiveConversion = product =>
    Boolean(product?.conversionId) && getSafeConversionFactor(product) > 0
  /**
   * Conversion direction contract:
   * - conversionFactor/toQty = number of counted units in 1 base unit
   *   (e.g. 1 keg = 88 pints => factor 88)
   * - Base -> Counted: multiply by factor
   * - Counted -> Base: divide by factor
   */
  const convertBaseToCounted = (baseQty, product) =>
    (Number(baseQty) || 0) * getSafeConversionFactor(product)
  const convertCountedToBase = (countedQty, product) =>
    (Number(countedQty) || 0) / getSafeConversionFactor(product)
  const getDisplayQty = (baseQty, product) =>
    hasActiveConversion(product)
      ? convertBaseToCounted(baseQty, product)
      : Number(baseQty) || 0
  const toBaseQtyFromDisplay = (displayQty, product) =>
    hasActiveConversion(product)
      ? convertCountedToBase(displayQty, product)
      : Number(displayQty) || 0
  const updateRowWithPatch = (targetProduct, patch) => {
    if (!targetProduct) return
    if (targetProduct._id) {
      let patchedProduct = null
      setProducts(prevProducts =>
        prevProducts.map(prod => {
          if (prod._id !== targetProduct._id) return prod
          patchedProduct = { ...prod, ...patch }
          return patchedProduct
        }),
      )
      setUpdatedProductsArray(prevUpdated => {
        const existingIndex = prevUpdated.findIndex(
          prod => prod._id === targetProduct._id,
        )
        if (existingIndex >= 0) {
          return prevUpdated.map((prod, index) =>
            index === existingIndex ? { ...prod, ...patch } : prod,
          )
        }
        if (!patchedProduct) return prevUpdated
        return [...prevUpdated, patchedProduct]
      })
      return
    }

    setProducts(prevProducts =>
      prevProducts.map(prod =>
        prod.title === targetProduct.title &&
        prod.supplierTitle === targetProduct.supplierTitle
          ? { ...prod, ...patch }
          : prod,
      ),
    )
    setAddedProductsArray(prevAdded =>
      prevAdded.map(prod =>
        prod.title === targetProduct.title &&
        prod.supplierTitle === targetProduct.supplierTitle
          ? { ...prod, ...patch }
          : prod,
      ),
    )
  }

  const handleUomSelectionChange = (product, selectedOption) => {
    const baseUom = product?.UoM || 'Unit'
    if (!selectedOption) {
      updateRowWithPatch(product, {
        conversionId: null,
        conversionName: '',
        conversionUnit: '',
        conversionBaseUnit: '',
        conversionAppliedAt: '',
        conversionFactor: 1,
        conversionInputQty: 0,
      })
      return
    }

    updateRowWithPatch(product, {
      conversionId: selectedOption.value,
      conversionName: selectedOption.conversionName,
      conversionUnit: selectedOption.conversionUnit,
      conversionBaseUnit: baseUom,
      conversionAppliedAt: new Date().toISOString(),
      conversionFactor: selectedOption.conversionFactor,
      conversionInputQty: formatDecimal(
        convertBaseToCounted(product?.inStock || 0, {
          conversionFactor: selectedOption.conversionFactor,
        }),
      ),
    })
  }

  const getConversionOptionsForProduct = product =>
    getConversionOptionsForStockProduct(product, venueProducts)

  const resolveCurrentStockTakeProduct = targetProduct => {
    if (!targetProduct) return null
    if (targetProduct._id) {
      return (
        products.find(product => product._id === targetProduct._id) ||
        targetProduct
      )
    }
    return (
      products.find(
        product =>
          product.title === targetProduct.title &&
          product.supplierTitle === targetProduct.supplierTitle,
      ) || targetProduct
    )
  }

  const handleOpenConversionModal = product => {
    if (isStockTakeLocked) {
      notification.info({
        message: 'Stock take is locked',
        description: 'This stock take is locked and view-only.',
        placement: 'topRight',
      })
      return
    }
    if (product?.isLinkedStockGroup) return
    if (isRecipeStockProduct(product)) return
    setConversionModalState({ visible: true, product })
  }

  const handleCloseConversionModal = () => {
    setConversionModalState({ visible: false, product: null })
  }

  const handleApplyConversionToRow = (targetProduct, selectedOption) => {
    const currentProduct = resolveCurrentStockTakeProduct(targetProduct)
    if (!currentProduct) return
    handleUomSelectionChange(currentProduct, selectedOption)
  }

  const handleConversionsUpdated = async ({
    stockTakeProduct,
    venueProductId,
    conversions,
    applyConversionId,
  }) => {
    await dispatch(fetchVenueProducts(venueId))
    const currentProduct = resolveCurrentStockTakeProduct(stockTakeProduct)
    if (!currentProduct) return

    const patch = {
      conversions,
      venueProductId,
    }
    updateRowWithPatch(currentProduct, patch)

    if (!applyConversionId) return
    const selectedOption = mapConversionRecordsToOptions(conversions).find(
      option => option.value === String(applyConversionId),
    )
    if (!selectedOption) return

    handleUomSelectionChange({ ...currentProduct, ...patch }, selectedOption)
  }

  // Helper function to calculate expected quantity
  const calculateExpectedQty = product => {
    try {
      if (!product) return 0
      const lastQty = Number(product.lastQuantity) || 0
      const received = Number(product.qtySinceLastStockTake) || 0
      const sold = Number(product.sold) || 0
      const wastage = Number(product.wastage) || 0

      const result = lastQty + received - sold - wastage

      // Handle invalid calculations
      if (isNaN(result) || !isFinite(result)) {
        console.warn('Invalid expected quantity calculation:', product)
        return 0
      }

      return result
    } catch (error) {
      console.error('Error calculating expected quantity:', error, product)
      return 0
    }
  }

  // Helper function to calculate variance
  const calculateVariance = product => {
    try {
      if (!product) {
        return { unitVariance: 0, percentVariance: 0, expectedQty: 0 }
      }

      const expectedQty = calculateExpectedQty(product)
      const inStock = Number(product.inStock) || 0

      // Handle invalid inputs
      if (isNaN(inStock) || !isFinite(inStock)) {
        console.warn('Invalid inStock value:', product)
        return { unitVariance: 0, percentVariance: 0, expectedQty }
      }

      const unitVariance = inStock - expectedQty
      let percentVariance = 0

      if (expectedQty !== 0 && isFinite(expectedQty)) {
        percentVariance = (unitVariance / expectedQty) * 100
        // Handle invalid percentage
        if (isNaN(percentVariance) || !isFinite(percentVariance)) {
          percentVariance = 0
        }
      }

      return { unitVariance, percentVariance, expectedQty }
    } catch (error) {
      console.error('Error calculating variance:', error, product)
      return { unitVariance: 0, percentVariance: 0, expectedQty: 0 }
    }
  }

  const renderLinkedFactorTooltip = record => {
    const mappings = Array.isArray(
      record?.linkedStockGroupProfile?.memberMappings,
    )
      ? record.linkedStockGroupProfile.memberMappings
      : []
    if (!mappings.length) {
      return 'This line combines multiple products for stock counting only.'
    }
    return (
      <div style={{ maxWidth: 360 }}>
        <div style={{ marginBottom: 6, fontWeight: 600 }}>
          Linked conversion factors
        </div>
        {mappings.map((mapping, index) => {
          const usesBase = Boolean(mapping?.useBaseUoM)
          const toQty = Number(mapping?.selectedToQty || 0)
          const toUnit = String(mapping?.selectedToUnit || '').trim()
          const baseUoM = String(mapping?.baseUoM || '').trim()
          const productTitle = String(mapping?.productTitle || '').trim()
          const productSize = String(mapping?.productSize || '').trim()
          const productUoM = String(mapping?.productUoM || '').trim()
          const memberLabel = productTitle
            ? `${productTitle}${
                productSize
                  ? ` (${productSize}${productUoM ? ` ${productUoM}` : ''})`
                  : ''
              }`
            : `Member ${index + 1}`
          const text = usesBase
            ? `${memberLabel}: base (${baseUoM || 'N/A'}) × 1`
            : `${memberLabel}: ${toQty > 0 ? toQty : 1} ${
                toUnit || 'unit'
              } per source unit`
          return (
            <div key={`linked-factor-${String(mapping?.productId || index)}`}>
              {text}
            </div>
          )
        })}
      </div>
    )
  }

  const columns = filterStockTakeCountModeColumns(
    filterStockTableColumns(
      reorderStockTakeTableColumns([
        {
          title: 'Title',
          dataIndex: 'title',
          key: 'title',
          width: STOCK_TAKE_TITLE_COLUMN_WIDTH,
          render: (_value, record) => {
            const packLabel = formatStockTakeLinePackLabel(
              record,
              getConversionOptionsForProduct(record),
            )
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span>{record.title}</span>
                {packLabel ? (
                  <span
                    data-testid='stock-take-line-pack'
                    style={{ color: '#6b7280', fontSize: 12, lineHeight: 1.3 }}
                  >
                    {packLabel}
                  </span>
                ) : null}
                {shouldShowDuplicatePackWarning(record) ? (
                  <span
                    data-testid='stock-take-duplicate-pack'
                    style={{ color: '#919191', fontSize: 11, lineHeight: 1.3 }}
                  >
                    Not the pack you last ordered
                  </span>
                ) : null}
                {isRecipeStockProduct(record) && (
                  <Tag color='gold' style={{ width: 'fit-content', margin: 0 }}>
                    Recipe
                  </Tag>
                )}
                {record.isLinkedStockGroup && (
                  <Tooltip title={renderLinkedFactorTooltip(record)}>
                    <Tag
                      color='purple'
                      style={{ width: 'fit-content', margin: 0 }}
                    >
                      Linked
                    </Tag>
                  </Tooltip>
                )}
              </div>
            )
          },
          sorter: (a, b) =>
            String(a.title).trim().localeCompare(String(b.title).trim()),
          sortDirections: ['ascend', 'descend'],
        },
        {
          title: renderHeaderWithTooltip(
            'Sold',
            'Quantity sold during this stock period. Can be manually entered or automatically calculated from sold recipes.',
          ),
          key: 'sold',
          className: 'inputColumn',
          width: '140px',
          render: product => {
            const soldWasEntered = stockSoldWasEntered(product)
            const displaySold = soldWasEntered
              ? getDisplayQty(product.sold, product)
              : null
            return (
              <Tooltip title='Quantity sold during this stock period. Can be manually entered or automatically calculated from sold recipes.'>
                <StockTakeInputNumber
                  field='sold'
                  productTitle={product.title}
                  value={displaySold}
                  onChange={value => {
                    const cleared =
                      value === null || value === undefined || value === ''
                    updateRowWithPatch(product, {
                      sold: cleared ? 0 : toBaseQtyFromDisplay(value, product),
                      soldEntered: !cleared,
                    })
                  }}
                  onFocus={e => e.target.select()}
                  min={0}
                  disabled={isStockTakeLocked}
                  style={{ margin: '0, 15px' }}
                  formatter={value => {
                    if (value === null || value === undefined || value === '')
                      return ''
                    const formatted = formatDecimal(value)
                    return formatted.toString()
                  }}
                />
              </Tooltip>
            )
          },
        },
        {
          title: renderHeaderWithTooltip(
            'In Stock',
            'Current quantity in stock (actual physical count)',
          ),
          key: 'inStock',
          className: 'inputColumn',
          width: '140px',
          render: product => {
            const counted = stockInStockWasEntered(product)
            const displayInStock = counted
              ? getDisplayQty(product.inStock, product)
              : null
            const unitSuffix = hasActiveConversion(product)
              ? ` (${product?.conversionUnit || ''})`
              : ''
            return (
              <Tooltip
                title={`Current quantity in stock (actual physical count)${unitSuffix}`}
              >
                <StockTakeInputNumber
                  field='in stock'
                  productTitle={product.title}
                  value={countedStockInputValue(
                    { ...product, inStockCounted: counted },
                    displayInStock,
                  )}
                  placeholder={STOCK_TAKE_UNCOUNTED_PLACEHOLDER}
                  onChange={value => {
                    const cleared =
                      value === null || value === undefined || value === ''
                    updateRowWithPatch(
                      product,
                      cleared
                        ? {
                            inStock: 0,
                            ...stockCountEnteredFlags(false),
                          }
                        : {
                            inStock: toBaseQtyFromDisplay(value, product),
                            ...stockCountEnteredFlags(true),
                          },
                    )
                  }}
                  onFocus={e => e.target.select()}
                  min={0}
                  disabled={isStockTakeLocked}
                  style={{ margin: '0, 15px' }}
                />
              </Tooltip>
            )
          },
        },
        {
          title: renderHeaderWithTooltip(
            'Par',
            stockTakeParColumnHelp(currentPlan),
          ),
          key: 'parLevel',
          className: 'inputColumn',
          width: '220px',
          render: product => {
            const canEdit = canEditStockTakeLinePar(product, venueProducts)
            const value = displayParLevelForStockLine(
              product,
              venueProducts,
              parEdits,
            )
            if (!canEdit) {
              const unavailableCopy = stockTakeParUnavailableCopy(
                product,
                venueProducts,
              )
              if (unavailableCopy) {
                return (
                  <Tooltip title={unavailableCopy}>
                    <span
                      data-testid='stock-take-par-not-on-list'
                      style={{
                        color: '#6b7280',
                        fontSize: 12,
                        lineHeight: 1.35,
                        display: 'inline-block',
                        maxWidth: 200,
                      }}
                    >
                      {unavailableCopy}
                    </span>
                  </Tooltip>
                )
              }
              return <span>—</span>
            }
            return (
              <Tooltip title={stockTakeParInputHelp(currentPlan)}>
                <StockTakeInputNumber
                  field='par'
                  productTitle={product.title}
                  data-testid={stockTakeParInputTestId(product)}
                  value={value}
                  placeholder='Not set'
                  onChange={next => {
                    const venueProduct = venueProductForStockTakeLine(
                      product,
                      venueProducts,
                    )
                    if (!venueProduct?._id) return
                    setParFocusLineId('')
                    setParEdits(prev => ({
                      ...prev,
                      [String(venueProduct._id)]: parseParLevelValue(next),
                    }))
                  }}
                  onFocus={e => e.target.select()}
                  min={0}
                  max={MAX_PAR_LEVEL}
                  disabled={isStockTakeLocked}
                  style={{ margin: '0, 15px', width: 88 }}
                />
              </Tooltip>
            )
          },
        },
        {
          title: (
            <div>
              {renderHeaderWithTooltip(
                'Wastage',
                'Quantity wasted or lost during this stock period',
              )}
              <StockWastageColumnHint
                venueId={venueId}
                stockTake={stockTake}
                stockTakeId={stockTakeId}
              />
            </div>
          ),
          key: 'wastage',
          className: 'inputColumn',
          width: '220px',
          render: product => {
            const fromTracker = stockWastageIsFromTracker(product, {
              isPartial: isPartialStockTake,
            })
            return (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <StockWastageQuantity
                    productId={product._id}
                    productTitle={product.title}
                    value={getDisplayQty(product.wastage, product)}
                    fromTracker={fromTracker}
                    includesVat={Boolean(product.hasVat)}
                    dishes={product.liveWastageDishes}
                    locked={isStockTakeLocked}
                    onChange={displayQty =>
                      onChangeStock(
                        product,
                        toBaseQtyFromDisplay(displayQty ?? 0, product),
                        'wastage',
                      )
                    }
                  />
                  <Popover
                    trigger='click'
                    title='Wastage comment'
                    content={
                      <div style={{ width: 260 }}>
                        <Input.TextArea
                          rows={3}
                          disabled={isStockTakeLocked}
                          value={product.wastageComment || ''}
                          placeholder='Add a note explaining why this item was wasted'
                          onChange={event =>
                            onChangeStock(
                              product,
                              event.target.value,
                              'wastageComment',
                            )
                          }
                        />
                        {!String(product.wastageComment || '').trim() && (
                          <InlineMutedText>No details provided</InlineMutedText>
                        )}
                      </div>
                    }
                  >
                    <Tooltip title='Add a note explaining why this item was wasted'>
                      <CommentOutlined
                        style={{
                          fontSize: 16,
                          color: product.wastageComment ? '#1677ff' : '#8c8c8c',
                          cursor: isStockTakeLocked ? 'not-allowed' : 'pointer',
                        }}
                      />
                    </Tooltip>
                  </Popover>
                </div>
                <StockWastageRecordLink
                  venueId={venueId}
                  stockTakeId={stockTakeId}
                  lineId={product._id}
                  venueProductId={stockLineRecordProductId(
                    product,
                    venueProducts,
                  )}
                  notInCatalogue={stockLineNotInCatalogue(
                    product,
                    venueProducts,
                  )}
                />
              </div>
            )
          },
        },
        {
          title: 'Size',
          dataIndex: 'size',
          key: 'size',
          className: 'smallColumn',
          width: '120px',
          sorter: (a, b) =>
            String(a.size).trim().localeCompare(String(b.size).trim()),
          sortDirections: ['ascend', 'descend'],
        },
        {
          title: 'Type',
          dataIndex: 'type',
          key: 'type',
          width: STOCK_TAKE_TYPE_COLUMN_WIDTH,
          filters: stockTakeProductsTypes.map(type => ({
            text: type,
            value: type.toLowerCase(),
          })),
          onFilter: (value, record) => {
            return record?.type.toLowerCase() === value
          },
        },
        {
          title: 'UoM',
          dataIndex: 'UoM',
          key: 'UoM',
          className: 'uomColumn',
          width: '160px',
          render: (_, product) => {
            if (product?.isLinkedStockGroup) {
              return (
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                >
                  <Tooltip title='Locked to linked profile UoM. Edit in Link Products.'>
                    <span>{unwrapStockTakeOriginalUom(product?.UoM)}</span>
                  </Tooltip>
                  {hasActiveConversion(product) ? (
                    <Tag color='blue' style={{ marginRight: 0 }}>
                      Converted
                    </Tag>
                  ) : null}
                  <Tag color='purple' style={{ marginRight: 0 }}>
                    Locked
                  </Tag>
                </div>
              )
            }
            const conversionOptions = getConversionOptionsForProduct(product)
            const hasConversion = hasActiveConversion(product)
            const baseUom = unwrapStockTakeOriginalUom(product?.UoM)
            const baseOptionValue = '__BASE_UOM__'
            const selectedUomValue = product?.conversionId || baseOptionValue
            const canManageConversions = shouldShowStockTakeConversionPencil({
              countMode: useCountMode,
              isLocked: isStockTakeLocked,
              isRecipe: isRecipeStockProduct(product),
              isLinkedStockGroup: Boolean(product?.isLinkedStockGroup),
            })
            const showConversionSelect =
              shouldShowStockTakeConversionSelect({
                countMode: useCountMode,
              }) && conversionOptions.length > 0
            const packText = formatStockTakeCountModePackText(
              product,
              conversionOptions,
            )
            return (
              <UomCell>
                <UomCellRow>
                  {showConversionSelect ? (
                    <UomSelect
                      value={selectedUomValue}
                      placeholder={baseUom}
                      disabled={isStockTakeLocked}
                      onChange={selectedValue => {
                        if (selectedValue === baseOptionValue) {
                          handleUomSelectionChange(product, null)
                          return
                        }
                        const selectedOption = conversionOptions.find(
                          option => option.value === selectedValue,
                        )
                        handleUomSelectionChange(
                          product,
                          selectedOption || null,
                        )
                      }}
                    >
                      <Select.Option value={baseOptionValue}>
                        {formatStockTakeUomSelectBaseLabel(product)}
                      </Select.Option>
                      {conversionOptions.map(option => (
                        <Select.Option key={option.value} value={option.value}>
                          {option.label}
                        </Select.Option>
                      ))}
                    </UomSelect>
                  ) : (
                    <UomLabel data-testid='stock-take-count-pack-text'>
                      {packText}
                    </UomLabel>
                  )}
                  {canManageConversions ? (
                    <Tooltip title='Manage unit conversions'>
                      <UomEditButton
                        type='button'
                        aria-label='Manage unit conversions'
                        onClick={() => handleOpenConversionModal(product)}
                      >
                        <EditOutlined />
                      </UomEditButton>
                    </Tooltip>
                  ) : null}
                </UomCellRow>
                {hasConversion &&
                  shouldShowStockTakeConversionSelect({
                    countMode: useCountMode,
                  }) && (
                    <Tooltip title='This row is currently displayed in converted units.'>
                      <Tag color='blue' style={{ marginRight: 0 }}>
                        Converted
                      </Tag>
                    </Tooltip>
                  )}
              </UomCell>
            )
          },
        },
        {
          title: 'Supplier',
          dataIndex: 'supplierTitle',
          key: 'supplier',
          width: STOCK_TAKE_SUPPLIER_COLUMN_WIDTH,
          render: supplierTitle => (
            <Tooltip title={supplierTitle || ''}>
              <span>{supplierTitle || ''}</span>
            </Tooltip>
          ),
          sorter: (a, b) =>
            String(a.supplierTitle)
              .trim()
              .localeCompare(String(b.supplierTitle).trim()),
          sortDirections: ['ascend', 'descend'],
        },
        {
          title: 'Linked',
          key: 'linkedActions',
          className: 'centerAlign',
          width: '120px',
          render: product => {
            if (!product?.isLinkedStockGroup || !product?.linkedStockGroupId) {
              return null
            }
            return (
              <Tooltip title='Remove this linked stock group'>
                <Button
                  type='link'
                  disabled={isStockTakeLocked}
                  onClick={() =>
                    handleUnlinkLinkedGroup(product.linkedStockGroupId)
                  }
                >
                  Unlink
                </Button>
              </Tooltip>
            )
          },
        },
        {
          title: 'Price',
          dataIndex: 'price',
          key: 'price',
          className: 'centerAlign',
          width: '120px',
          render: price => {
            return `£${price}`
          },
          sorter: (a, b) =>
            (a.hasVat ? VAT_RATE * a.price : a.price) -
            (b.hasVat ? VAT_RATE * b.price : b.price),
          sortDirections: ['ascend', 'descend'],
        },
        {
          title: 'SKU',
          dataIndex: 'sku',
          key: 'sku',
          className: 'smallColumn',
          width: '120px',
        },
        {
          title: renderHeaderWithTooltip(
            'Prev qty',
            'Previous quantity from the last stock take',
          ),
          dataIndex: 'lastQuantity',
          key: 'lastQuantity',
          className: 'centerAlign smallColumn',
          width: '140px',
          sorter: (a, b) => a.lastQuantity - b.lastQuantity,
          sortDirections: ['ascend', 'descend'],
          render: (item, product) => (
            <Tooltip title='Previous quantity from the last stock take'>
              <span>{formatDecimal(getDisplayQty(item, product))}</span>
            </Tooltip>
          ),
        },
        {
          title: renderHeaderWithTooltip(
            "Rec'd",
            'Quantity received during this stock period',
          ),
          dataIndex: 'qtySinceLastStockTake',
          key: 'qtySinceLastStockTake',
          width: '120px',
          className: 'centerAlign smallColumn',
          sorter: (a, b) => a.qtySinceLastStockTake - b.qtySinceLastStockTake,
          sortDirections: ['ascend', 'descend'],
          render: (item, product) => {
            const displayItem = getDisplayQty(item, product)
            const isDecimal = Math.ceil(displayItem) !== displayItem
            let displayValue
            if (isDecimal) {
              const numberWithToFixed = Number(displayItem).toFixed(2)
              const stringifyNumber = String(numberWithToFixed)
              displayValue = stringifyNumber.endsWith('0')
                ? Number(stringifyNumber.slice(0, stringifyNumber.length - 1))
                : numberWithToFixed
            } else {
              displayValue = displayItem
            }
            return (
              <Tooltip title='Quantity received during this stock period'>
                <span>{displayValue}</span>
              </Tooltip>
            )
          },
        },
        {
          title: renderHeaderWithTooltip(
            'Expected Qty',
            'Expected Quantity = Last Stock + Received – Sold – Wastage',
          ),
          key: 'expectedQty',
          className: 'centerAlign',
          width: '150px',
          render: product => {
            try {
              if (wasteExceedsStockOnHand(product)) {
                return (
                  <span
                    data-testid={
                      product?._id
                        ? `stock-expected-over-waste-${product._id}`
                        : 'stock-expected-over-waste'
                    }
                    style={{
                      color: '#8c8c8c',
                      fontSize: 12,
                      lineHeight: 1.3,
                      whiteSpace: 'normal',
                    }}
                  >
                    {WASTAGE_COPY.wasteBiggerThanOnHand}
                  </span>
                )
              }
              const expectedQtyBase = calculateExpectedQty(product)
              const displayQty = hasActiveConversion(product)
                ? convertBaseToCounted(expectedQtyBase, product)
                : expectedQtyBase
              const isNegative = displayQty < 0
              const formattedQty = formatDecimal(displayQty)
              const conversionSuffix = hasActiveConversion(product)
                ? ` ${product?.conversionUnit || ''}`.trim()
                : ''
              const expectedDisplay = formatStockTakeExpectedQtyDisplay({
                expectedQty: expectedQtyBase,
                unitDisplay: `${formattedQty}${
                  conversionSuffix ? ` ${conversionSuffix}` : ''
                }`,
              })
              return (
                <Tooltip title={expectedDisplay.hint}>
                  <span
                    data-testid={
                      expectedDisplay.unreliable
                        ? 'stock-take-expected-check-units'
                        : undefined
                    }
                    style={{
                      color: expectedDisplay.unreliable
                        ? 'inherit'
                        : isNegative
                        ? '#ff4d4f'
                        : 'inherit',
                    }}
                  >
                    {expectedDisplay.text}
                  </span>
                </Tooltip>
              )
            } catch (error) {
              console.error('Error rendering Expected Qty:', error, product)
              return <span style={{ color: '#ff4d4f' }}>Error</span>
            }
          },
          sorter: (a, b) => {
            const expectedQtyA = calculateExpectedQty(a)
            const expectedQtyB = calculateExpectedQty(b)
            return expectedQtyA - expectedQtyB
          },
          sortDirections: ['ascend', 'descend'],
        },
        {
          title: renderHeaderWithTooltip(
            'Stock value',
            'Total monetary value of current stock (In Stock × Price)',
          ),
          key: 'stockAmount',
          className: 'centerAlign',
          width: '140px',
          render: product => {
            const stockValue = product.hasVat
              ? getLineStockQuantityValue(product, product.inStock) * VAT_RATE
              : getLineStockQuantityValue(product, product.inStock)
            const label = stockLineMoneyLabel(product, formatGbp(stockValue))
            return (
              <Tooltip title='Total monetary value of current stock (In Stock × Price)'>
                <span
                  data-testid={
                    product?._id ? `stock-value-${product._id}` : 'stock-value'
                  }
                >
                  {label}
                </span>
              </Tooltip>
            )
          },
          sorter: (a, b) =>
            (a.hasVat
              ? getLineStockQuantityValue(a, a.inStock) * VAT_RATE
              : getLineStockQuantityValue(a, a.inStock)) -
            (b.hasVat
              ? getLineStockQuantityValue(b, b.inStock) * VAT_RATE
              : getLineStockQuantityValue(b, b.inStock)),
          sortDirections: ['ascend', 'descend'],
        },
        {
          title: renderHeaderWithTooltip(
            'Wastage value',
            'Total monetary value of wasted stock (Wastage × Price)',
          ),
          key: 'wastageAmount',
          className: 'centerAlign',
          width: '140px',
          render: product => {
            const wastageValue = product.hasVat
              ? getLineStockQuantityValue(product, product.wastage) * VAT_RATE
              : getLineStockQuantityValue(product, product.wastage)
            return (
              <Tooltip title='Total monetary value of wasted stock (Wastage × Price)'>
                <span>{formatGbp(wastageValue)}</span>
              </Tooltip>
            )
          },
          sorter: (a, b) =>
            (a.hasVat
              ? getLineStockQuantityValue(a, a.wastage) * VAT_RATE
              : getLineStockQuantityValue(a, a.wastage)) -
            (b.hasVat
              ? getLineStockQuantityValue(b, b.wastage) * VAT_RATE
              : getLineStockQuantityValue(b, b.wastage)),
          sortDirections: ['ascend', 'descend'],
        },
        {
          title: renderHeaderWithTooltip(
            'Variance',
            'Variance = In Stock - Expected Quantity. Shows the difference between actual and expected stock levels.',
          ),
          key: 'variance',
          className: 'centerAlign',
          width: '150px',
          render: product => {
            try {
              if (!stockInStockWasEntered(product)) {
                return (
                  <span
                    data-testid={
                      product?._id
                        ? `stock-variance-quiet-${product._id}`
                        : 'stock-variance-quiet'
                    }
                  />
                )
              }
              if (wasteExceedsStockOnHand(product)) {
                return (
                  <span
                    data-testid={
                      product?._id
                        ? `stock-variance-over-waste-${product._id}`
                        : 'stock-variance-over-waste'
                    }
                    style={{
                      color: '#8c8c8c',
                      fontSize: 12,
                      lineHeight: 1.3,
                      whiteSpace: 'normal',
                    }}
                  >
                    {WASTAGE_COPY.wasteBiggerThanOnHand}
                  </span>
                )
              }
              const { unitVariance, percentVariance, expectedQty } =
                calculateVariance(product)
              const displayVariance = hasActiveConversion(product)
                ? convertBaseToCounted(unitVariance, product)
                : unitVariance
              let color = '#8c8c8c' // grey for zero
              if (displayVariance > 0) {
                color = '#52c41a' // green for positive
              } else if (displayVariance < 0) {
                color = '#ff4d4f' // red for negative
              }
              const safeUnitVariance =
                isNaN(displayVariance) || !isFinite(displayVariance)
                  ? 0
                  : formatDecimal(displayVariance)
              const safePercentVariance =
                isNaN(percentVariance) || !isFinite(percentVariance)
                  ? 0
                  : formatDecimal(percentVariance)
              const conversionSuffix = hasActiveConversion(product)
                ? ` ${product?.conversionUnit || ''}`.trim()
                : ''
              const varianceDisplay = formatStockTakeVarianceDisplay({
                expectedQty,
                unitDisplay: `${safeUnitVariance}${
                  conversionSuffix ? ` ${conversionSuffix}` : ''
                }`,
                percentDisplay: String(safePercentVariance),
              })
              return (
                <Tooltip title={varianceDisplay.hint}>
                  <span
                    data-testid={
                      varianceDisplay.unreliable
                        ? 'stock-take-variance-check-units'
                        : undefined
                    }
                    style={{
                      color: varianceDisplay.unreliable ? 'inherit' : color,
                    }}
                  >
                    {varianceDisplay.text}
                  </span>
                </Tooltip>
              )
            } catch (error) {
              console.error('Error rendering Variance:', error, product)
              return <span style={{ color: '#ff4d4f' }}>Error</span>
            }
          },
          sorter: (a, b) => {
            const varianceA = calculateVariance(a)
            const varianceB = calculateVariance(b)
            return varianceA.unitVariance - varianceB.unitVariance
          },
          sortDirections: ['ascend', 'descend'],
        },
        {
          title: renderHeaderWithTooltip(
            "Wgt'd",
            'Indicates if this product is weighted (measured in kg or g)',
          ),
          key: 'weighted',
          className: 'centerAlign',
          width: '100px',
          render: product => {
            const includesInUoM = product.UoM
              ? product.UoM.toLowerCase() === 'kg' ||
                product.UoM.toLowerCase() === 'g'
              : false
            return (
              <Tooltip title='Indicates if this product is weighted (measured in kg or g)'>
                <span>{includesInUoM ? <GoldenFilled /> : ''}</span>
              </Tooltip>
            )
          },
        },
        {
          title: 'VAT',
          key: 'total_vat',
          className: 'centerAlign',
          width: '120px',
          render: product => {
            const unitPrice = getEffectiveStockUnitPrice(product)
            const vat = product.hasVat ? unitPrice * VAT_PERCENTAGE : 0
            const label = stockLineMoneyLabel(
              product,
              `£${(vat * product.inStock).toFixed(2)}`,
            )
            return (
              <span
                data-testid={
                  product?._id ? `stock-vat-${product._id}` : 'stock-vat'
                }
              >
                {label}
              </span>
            )
          },
          sorter: (a, b) =>
            (a.hasVat
              ? getEffectiveStockUnitPrice(a) * VAT_PERCENTAGE * a.inStock
              : 0) -
            (b.hasVat
              ? getEffectiveStockUnitPrice(b) * VAT_PERCENTAGE * b.inStock
              : 0),
          sortDirections: ['ascend', 'descend'],
        },
        {
          title: 'Net',
          key: 'total_net',
          className: 'centerAlign',
          width: '120px',
          render: product => {
            const label = stockLineMoneyLabel(
              product,
              `£${getLineStockQuantityValue(product, product.inStock).toFixed(
                2,
              )}`,
            )
            return (
              <span
                data-testid={
                  product?._id ? `stock-net-${product._id}` : 'stock-net'
                }
              >
                {label}
              </span>
            )
          },
          sorter: (a, b) =>
            getLineStockQuantityValue(a, a.inStock) -
            getLineStockQuantityValue(b, b.inStock),
          sortDirections: ['ascend', 'descend'],
        },
        {
          title: 'Gross',
          key: 'total_price',
          className: 'centerAlign',
          width: '120px',
          render: product => {
            const unitPrice = getEffectiveStockUnitPrice(product)
            const priceWithVat = product.hasVat
              ? unitPrice * VAT_RATE
              : unitPrice
            const label = stockLineMoneyLabel(
              product,
              `£${(priceWithVat * product.inStock).toFixed(2)}`,
            )
            return (
              <span
                data-testid={
                  product?._id ? `stock-gross-${product._id}` : 'stock-gross'
                }
              >
                {label}
              </span>
            )
          },
          sorter: (a, b) =>
            (a.hasVat
              ? VAT_RATE * getEffectiveStockUnitPrice(a) * a.inStock
              : getEffectiveStockUnitPrice(a) * a.inStock) -
            (b.hasVat
              ? VAT_RATE * getEffectiveStockUnitPrice(b) * b.inStock
              : getEffectiveStockUnitPrice(b) * b.inStock),
          sortDirections: ['ascend', 'descend'],
        },
      ]),
      canViewExpectedAndVariance,
    ),
    useCountMode,
  )

  const venueProducts = useSelector(state => state.venueProducts.products)
  const [updatedProductsArray, setUpdatedProductsArray] = useState([])
  const [addedProductsArray, setAddedProductsArray] = useState([])
  const _file = useRef(null)
  const csvMenuLinkRef = useRef(null)
  const [lastSavedAt, setLastSavedAt] = useState(null)
  const [savedThisVisit, setSavedThisVisit] = useState(false)
  const [parFocusLineId, setParFocusLineId] = useState('')

  useEffect(() => {
    const fromRecord = stockTakeSavedAtFromRecord(stockTake, stockTakeProducts)
    if (!fromRecord) return
    setLastSavedAt(prev => prev || fromRecord)
  }, [stockTake, stockTakeProducts])

  useEffect(() => {
    dispatch(fetchVenueProducts(venueId))
    dispatch(fetchAllRecipes(venueId))
  }, [])

  useEffect(() => {
    dispatch(fetchStockTakeProducts(venueId, stockTakeId))
  }, [dispatch, venueId])

  const loadOrderToParReview = useCallback(async () => {
    const payload = await dispatch(
      fetchOrderToParSuggestions(venueId, stockTakeId),
    )
    setOrderToParRecommendations(
      Array.isArray(payload?.recommendations) ? payload.recommendations : [],
    )
    setOrderToParSkipped(Array.isArray(payload?.skipped) ? payload.skipped : [])
    setOrderToParAskOllie(payload?.askOllie || null)
    setOrderToParLoaded(true)
    setOrderToParRemovedKeys([])
    setOrderToParQtyByKey({})
  }, [dispatch, venueId, stockTakeId])

  useEffect(() => {
    loadOrderToParReview()
  }, [loadOrderToParReview])

  useEffect(() => {
    setShowFullCountGrid(false)
    setShowOtherPacks(false)
  }, [stockTakeId])

  const stockTakeFound = Boolean(stockTake)
  useEffect(() => {
    if (stockTakeFound) return undefined
    let cancelled = false
    setStockTakeLookupDone(false)
    const lookUp = async () => {
      try {
        await dispatch(fetchStockTakes(venueId))
      } finally {
        if (!cancelled) setStockTakeLookupDone(true)
      }
    }
    lookUp()
    return () => {
      cancelled = true
    }
  }, [dispatch, venueId, stockTakeId, stockTakeFound])

  const detailState = stockTakeDetailState({
    stockTake,
    lookupDone: stockTakeLookupDone,
  })

  useEffect(() => {
    const dummyClears = patchDummyStockTakeNotes(stockTake)
    setStockTakeMetadata({
      siteName: stockTake?.siteName || '',
      siteManager: dummyClears.siteManager ?? (stockTake?.siteManager || ''),
      lineManager: dummyClears.lineManager ?? (stockTake?.lineManager || ''),
      countedByName: stockTake?.countedByName || '',
      reasonForCount: stockTake?.reasonForCount || '',
      countNotes: dummyClears.countNotes ?? (stockTake?.countNotes || ''),
    })
  }, [
    stockTake?.siteName,
    stockTake?.siteManager,
    stockTake?.lineManager,
    stockTake?.countedByName,
    stockTake?.reasonForCount,
    stockTake?.countNotes,
  ])

  const dummyNotesClearRef = useRef('')
  useEffect(() => {
    if (!shouldPersistDummyStockTakeNoteClear(stockTake)) return undefined
    const takeId = String(stockTake._id)
    if (dummyNotesClearRef.current === takeId) return undefined
    dummyNotesClearRef.current = takeId
    dispatch(
      updateStockTake(
        venueId,
        { _id: takeId, ...patchDummyStockTakeNotes(stockTake) },
        'CLEAR_DUMMY_NOTES',
      ),
    )
    return undefined
  }, [
    dispatch,
    stockTake,
    stockTake?._id,
    stockTake?.countNotes,
    stockTake?.lineManager,
    stockTake?.siteManager,
    venueId,
  ])

  useEffect(() => {
    setProducts(stockTakeProducts)

    if (stockTakeProducts.length !== 0) {
      const allTruthyTypes = stockTakeProducts
        .map(product =>
          product.type
            ? wordCaps(product?.type).trim()
            : wordCaps(product?.product?.type).trim(),
        )
        .filter(type => Boolean(type))

      const uniqueProductTypes = [...new Set(allTruthyTypes)]

      setProductsTypes(uniqueProductTypes)
    }
  }, [stockTakeProducts])

  const collapsedStockTakePacks = useMemo(
    () => listVisibleStockTakePackLines(products, { collapse: true }),
    [products],
  )
  const collapseUnusedPacks =
    isSupplierScopedStockTake(stockTake) && !showOtherPacks && !belowParOnly
  const { visible: visibleStockTakeProducts } = useMemo(
    () =>
      listVisibleStockTakePackLines(products, {
        collapse: collapseUnusedPacks,
      }),
    [collapseUnusedPacks, products],
  )
  const hiddenOtherPackCount = isSupplierScopedStockTake(stockTake)
    ? collapsedStockTakePacks.hiddenCount
    : 0
  const otherPacksToggleLabel = formatOtherPacksToggleLabel(
    hiddenOtherPackCount,
    showOtherPacks,
  )
  const venueProductsWithDraftPars = useMemo(
    () => applyStockTakeParEdits(venueProducts, parEdits),
    [venueProducts, parEdits],
  )
  const belowParSoFar = useMemo(
    () => summarizeBelowParSoFar(products, venueProductsWithDraftPars),
    [products, venueProductsWithDraftPars],
  )
  const displayedStockTakeProducts = useMemo(
    () =>
      belowParOnly
        ? sortBelowParCountedLines(
            visibleStockTakeProducts,
            venueProductsWithDraftPars,
          )
        : visibleStockTakeProducts,
    [belowParOnly, venueProductsWithDraftPars, visibleStockTakeProducts],
  )

  useEffect(() => {
    if (!parFocusLineId || showOtherPacks) return
    const focusedVisible = visibleStockTakeProducts.some(
      record =>
        String(record?._id || record?.lineId || '') === String(parFocusLineId),
    )
    if (!focusedVisible) setShowOtherPacks(true)
  }, [parFocusLineId, showOtherPacks, visibleStockTakeProducts])

  useEffect(() => {
    if (!parFocusLineId) return undefined
    const line = products.find(
      record =>
        String(record?._id || record?.lineId || '') === String(parFocusLineId),
    )
    if (!line) return undefined
    const timer = window.setTimeout(() => focusStockTakeParInput(line), 50)
    return () => window.clearTimeout(timer)
  }, [displayedStockTakeProducts, parFocusLineId, products])

  useEffect(() => {
    const loadPartialScope = async () => {
      if (!isPartialStockTake) {
        setPartialScopeProductKeys([])
        setPartialGroupLabel('')
        return
      }
      const partialGroupId =
        stockTake?.partialGroupId?._id || stockTake?.partialGroupId || null
      if (!partialGroupId) {
        setPartialScopeProductKeys([])
        setPartialGroupLabel('')
        return
      }
      try {
        const groups = await API.get('api2', `venue/${venueId}/stocks/groups`)
        const matchingGroup = (Array.isArray(groups) ? groups : []).find(
          group => {
            const groupId = group?._id || group?.id
            return String(groupId || '') === String(partialGroupId)
          },
        )
        const keys = Array.isArray(matchingGroup?.productKeys)
          ? matchingGroup.productKeys
              .map(normalizeStoredScopeKey)
              .filter(Boolean)
          : []
        setPartialScopeProductKeys([...new Set(keys)])
        setPartialGroupLabel(String(matchingGroup?.name || '').trim())
      } catch (error) {
        console.error('Failed loading partial group scope keys:', error)
        setPartialScopeProductKeys([])
        setPartialGroupLabel('')
      }
    }

    loadPartialScope()
  }, [isPartialStockTake, stockTake?.partialGroupId, venueId])

  const hasUnsavedChanges = stockTakeDraftIsDirty({
    draftLines: products,
    savedLines: stockTakeProducts,
    addedCount: addedProductsArray.length,
    parEdits,
    venueProducts,
    plan: currentPlan,
    venueId,
  })
  const canSaveTake = hasUnsavedChanges && !isStockTakeLocked
  const SaveTakeButton = canSaveTake ? StockPrimaryButton : StockQuietButton

  const countProgress = formatStockTakeCountProgress(products)
  const hasPersistedCount = stockTakeHasPersistedCount(
    stockTake,
    stockTakeProducts,
  )
  const parLevelUsageText = formatParLevelUsage(
    countVenueProductsWithParLevel(venueProductsWithDraftPars),
    currentPlan,
  )
  const stockTakeSupplierIds = uniqueStockTakeSupplierIds(
    stockTake?.supplierIds,
  )
  const missingParProducts = useMemo(
    () =>
      listMissingParProductsForSuppliers({
        venueProducts,
        stockTakeLines: products,
        supplierIds: stockTakeSupplierIds,
      }),
    [venueProducts, products, stockTakeSupplierIds],
  )
  const missingParStripCopy = formatMissingParStripCopy(
    missingParProducts.length,
    { supplierCount: stockTakeSupplierIds.length },
  )
  const askOllieAfterSavedCount =
    hasSavedCountedStockTakeLine(stockTakeProducts)
  const liveOrderToPar = useMemo(
    () =>
      previewOrderToParFromStockLines({
        lines: products,
        venueProducts,
        parEdits,
        // Basket + still-due from last Express OTP so preview matches Save.
        coverage: coverageMapsFromOrderToParItems([
          ...(Array.isArray(orderToParRecommendations)
            ? orderToParRecommendations
            : []),
          ...(Array.isArray(orderToParSkipped) ? orderToParSkipped : []),
        ]),
      }),
    [
      orderToParRecommendations,
      orderToParSkipped,
      parEdits,
      products,
      venueProducts,
    ],
  )
  const orderToParReviewRows = useMemo(
    () =>
      hasUnsavedChanges
        ? liveOrderToPar.recommendations
        : reviewOrderToParList(orderToParRecommendations, {
            removedKeys: orderToParRemovedKeys,
            qtyByKey: orderToParQtyByKey,
          }),
    [
      hasUnsavedChanges,
      liveOrderToPar.recommendations,
      orderToParQtyByKey,
      orderToParRecommendations,
      orderToParRemovedKeys,
    ],
  )
  const orderToParReviewSummary = useMemo(
    () => summarizeOrderToParReview(orderToParReviewRows),
    [orderToParReviewRows],
  )
  const orderToParEmptyState = useMemo(
    () =>
      pickOrderToParEmptyState({
        recommendations: hasUnsavedChanges
          ? liveOrderToPar.recommendations
          : orderToParRecommendations,
        skipped: hasUnsavedChanges ? liveOrderToPar.skipped : orderToParSkipped,
        reviewRows: orderToParReviewRows,
      }),
    [
      hasUnsavedChanges,
      liveOrderToPar.recommendations,
      liveOrderToPar.skipped,
      orderToParRecommendations,
      orderToParReviewRows,
      orderToParSkipped,
    ],
  )
  const orderToParEmptyMessage = orderToParEmptyCopy(orderToParEmptyState)
  const inactiveOrderToParSkippedCount = countInactiveShoppingListSkipped(
    hasUnsavedChanges ? liveOrderToPar.skipped : orderToParSkipped,
  )
  const orderToParAskOllieChip = useMemo(
    () =>
      formatOrderToParAskOllieChip({
        empty: Boolean(orderToParEmptyState),
        recommendations: orderToParReviewRows,
        supplierTitles: stockTake?.supplierTitles,
      }),
    [orderToParEmptyState, orderToParReviewRows, stockTake?.supplierTitles],
  )
  const orderToParStripCopy = formatOrderToParStripCopy({
    emptyMessage: orderToParEmptyMessage,
    belowParCount: orderToParReviewSummary.belowParCount,
    estimatedValueLabel: formatGbp(orderToParReviewSummary.estimatedValue),
  })
  const orderToParLockCopy = formatOrderToParLockSummary({
    belowParCount: orderToParReviewSummary.belowParCount,
    estimatedValueLabel: formatGbp(orderToParReviewSummary.estimatedValue),
  })
  const canAddOrderToParToBasket = orderToParBasketIsReady({
    isLocked: isStockTakeLocked,
    loaded: orderToParLoaded,
    emptyState: orderToParEmptyState,
    belowParCount: orderToParReviewSummary.belowParCount,
    hasUnsavedChanges,
  })

  const lastEditedByText =
    displayStockTakeLastEditedBy({
      stockTake,
      currentUserName,
      savedThisVisit,
    }) || stockTakeLastEditedBy(stockTake)
  const displayedSavedAt = stockTakeDisplayedSavedAt(stockTake, lastSavedAt)

  const lockTimestamp = stockTake?.lockedAt
    ? dayjs(stockTake.lockedAt).format('DD MMM YYYY, HH:mm')
    : null
  const showLockTime = shouldShowStockTakeLockTime(isStockTakeLocked)
  const takeMetaRows = stockTakeVisibleMetaRows({
    ...stockTakeMetadata,
    reasonForCount: displayStockTakeReasonForCount(stockTake),
  })

  async function uploadStockTakeFile() {
    if (isStockTakeLocked) {
      notification.info({
        message: 'Stock take is locked',
        description: 'This stock take is locked and view-only.',
        placement: 'topRight',
      })
      return
    }
    const file = _file.current.files[0]
    if (!file) {
      alert('Choose a file first')
      return
    }

    const extension = getStockTakeFileExtension(file)
    const supportedExtensions = ['csv', 'xlsx', 'xls']

    if (!supportedExtensions.includes(extension)) {
      notification.error({
        message: 'Unsupported file type',
        description:
          'Please upload a CSV or Excel file (.csv, .xlsx, or .xls).',
        placement: 'topRight',
      })
      if (_file.current) _file.current.value = ''
      return
    }

    try {
      const rows =
        extension === 'csv'
          ? await parseStockTakeCsvFile(file)
          : await parseStockTakeExcelFile(file)

      await processStockTakeRows(rows, { stockTakeId, venueId, dispatch })
    } catch (error) {
      console.error('Error parsing stock take file:', error)
      const isExcel = extension === 'xlsx' || extension === 'xls'
      notification.error({
        message: isExcel ? 'Excel parsing failed' : 'CSV parsing failed',
        description: isExcel
          ? 'Could not read the Excel file. Please ensure it is a valid .xlsx or .xls file and try again.'
          : 'Could not parse the CSV file. Please ensure it is a valid CSV file and try again.',
        placement: 'topRight',
      })
    }

    if (_file.current) _file.current.value = ''
  }

  const handleOpenSoldRecipesModal = () => {
    if (isStockTakeLocked) {
      notification.info({
        message: 'Stock take is locked',
        description: 'This stock take is locked and view-only.',
        placement: 'topRight',
      })
      return
    }
    setSoldRecipes(stockTake?.soldRecipes || [])
    setAddSoldRecipesModalVisible(true)
  }

  const handleOpenWastedRecipesModal = () => {
    if (isStockTakeLocked) {
      notification.info({
        message: 'Stock take is locked',
        description: 'This stock take is locked and view-only.',
        placement: 'topRight',
      })
      return
    }
    setWastedRecipes(stockTake?.wastedRecipes || [])
    setAddWastedRecipesModalVisible(true)
  }

  const handleSaveSoldRecipes = soldRecipesData => {
    try {
      const normalizedSoldRecipes = soldRecipesData || []
      setSoldRecipes(normalizedSoldRecipes)

      // If the user didn't enter any positive quantities, there's nothing to apply
      if (
        !normalizedSoldRecipes.length ||
        !normalizedSoldRecipes.some(sr => (Number(sr.qtySold) || 0) > 0)
      ) {
        notification.info({
          message: 'No sold quantities entered',
          description:
            'No positive sold quantities were entered, so nothing was applied from Sold Recipes.',
          placement: 'topRight',
          duration: 4,
        })
        setAddSoldRecipesModalVisible(false)
        return
      }

      // Calculate ingredient usage based on sold recipes and full recipe data
      const ingredientUsageMap = calculateIngredientUsage(
        normalizedSoldRecipes,
        recipes,
      )

      if (
        !ingredientUsageMap ||
        Object.keys(ingredientUsageMap || {}).length === 0
      ) {
        notification.warning({
          message: 'No ingredient usage calculated',
          description:
            'We could not calculate any ingredient usage from the sold recipes. Please check that your recipes have ingredients and portions set up.',
          placement: 'topRight',
          duration: 6,
        })
        setAddSoldRecipesModalVisible(false)
        return
      }

      // Update "Sold" for existing ingredients and auto-add missing ingredients
      const usageEntries = Object.values(ingredientUsageMap || {})
      const matchedEntryKeys = new Set()

      const normalizeComparable = value =>
        String(value || '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, ' ')
          .trim()
      const normalizeUnit = unit =>
        String(unit || '')
          .toLowerCase()
          .replace(/[^a-z]+/g, '')
          .trim()
      const unitToBaseFactor = unit => {
        const normalized = normalizeUnit(unit)
        const factors = {
          g: 1,
          gram: 1,
          grams: 1,
          kg: 1000,
          kilo: 1000,
          kilos: 1000,
          kilogram: 1000,
          kilograms: 1000,
          ml: 1,
          millilitre: 1,
          millilitres: 1,
          milliliter: 1,
          milliliters: 1,
          l: 1000,
          litre: 1000,
          litres: 1000,
          liter: 1000,
          liters: 1000,
        }
        return factors[normalized] || null
      }
      const convertUsageToRowUnit = (quantity, fromUnit, toUnit) => {
        const qty = Number(quantity) || 0
        if (!qty) return 0
        const normalizedFrom = normalizeUnit(fromUnit)
        const normalizedTo = normalizeUnit(toUnit)
        if (
          !normalizedFrom ||
          !normalizedTo ||
          normalizedFrom === normalizedTo
        ) {
          return qty
        }
        const fromFactor = unitToBaseFactor(normalizedFrom)
        const toFactor = unitToBaseFactor(normalizedTo)
        if (!fromFactor || !toFactor) return qty
        return (qty * fromFactor) / toFactor
      }
      // Helper function to normalize supplier names for comparison
      const normalizeSupplier = supplier => normalizeComparable(supplier)
      const normalizeSku = sku => normalizeComparable(sku).replace(/\s+/g, '')
      const normalizeTitle = title => normalizeComparable(title)
      const isSupplierCompatible = (left, right) => {
        if (!left || !right) return true
        return left === right || left.includes(right) || right.includes(left)
      }

      // Helper function to create a match key for tracking matched entries
      const createMatchKey = entry => {
        // Use ingredientId as primary key, or fallback to sku+supplier combination
        return (
          entry.ingredientId ||
          `${entry.sku}::${normalizeSupplier(entry.supplierTitle)}`
        )
      }
      const matchesLinkedUsageEntry = (prod, entry) => {
        if (!prod?.isLinkedStockGroup || !entry) return false
        if (
          Array.isArray(prod?.linkedVenueProductIds) &&
          prod.linkedVenueProductIds.length > 0 &&
          entry?.ingredientId
        ) {
          const isLinkedIdMatch = prod.linkedVenueProductIds.some(
            linkedId => String(linkedId) === String(entry.ingredientId),
          )
          if (isLinkedIdMatch) return true
        }
        if (
          Array.isArray(prod?.linkedStockGroupProfile?.memberMappings) &&
          prod.linkedStockGroupProfile.memberMappings.length > 0
        ) {
          const entrySku = normalizeSku(entry?.sku)
          const entryTitle = normalizeTitle(entry?.title)
          const entrySupplier = normalizeSupplier(entry?.supplierTitle)
          return prod.linkedStockGroupProfile.memberMappings.some(member => {
            const memberSku = normalizeSku(member?.productSku)
            const memberTitle = normalizeTitle(member?.productTitle)
            const memberSupplier = normalizeSupplier(
              member?.productSupplierTitle,
            )
            if (entrySku && memberSku && entrySku === memberSku) {
              return isSupplierCompatible(entrySupplier, memberSupplier)
            }
            if (entryTitle && memberTitle && entryTitle === memberTitle) {
              return isSupplierCompatible(entrySupplier, memberSupplier)
            }
            return false
          })
        }
        return false
      }

      // 1) Update existing products' Sold values using improved matching logic:
      //    - First try to match by product ID (if available on stock product)
      //    - Otherwise match by SKU + Supplier (both must match)
      const updatedProducts = products.map(prod => {
        if (hasPartialScopeRestriction) {
          const scopeKey = buildScopeKey(prod)
          if (!partialScopeProductKeys.includes(scopeKey)) {
            return prod
          }
        }
        // Try to find matching usage entries
        let usageEntry = null
        let usageEntriesForProduct = []

        // Strategy 1: Try to match by product ID if available
        // Note: Currently StockTakeProduct doesn't store a product reference,
        // but if we add one in the future (e.g., venueProductId), we can check here
        // For now, we'll check if prod has any ID field that might match ingredientId
        const prodProductId =
          prod.venueProductId || prod.productId || prod.product?._id

        if (prodProductId) {
          usageEntry = usageEntries.find(
            entry =>
              entry.ingredientId &&
              entry.ingredientId.toString() === prodProductId.toString(),
          )
        }

        if (prod?.isLinkedStockGroup) {
          usageEntriesForProduct = usageEntries.filter(entry => {
            const matchKey = createMatchKey(entry)
            if (matchedEntryKeys.has(matchKey)) return false
            return matchesLinkedUsageEntry(prod, entry)
          })
          if (!usageEntriesForProduct.length && usageEntry) {
            const usageKey = createMatchKey(usageEntry)
            if (!matchedEntryKeys.has(usageKey)) {
              usageEntriesForProduct = [usageEntry]
            }
          }
        }

        // Strategy 2: If no product ID match, match by SKU + Supplier
        if (!usageEntry && prod.sku) {
          const prodSku = normalizeSku(prod.sku)
          const prodSupplier = normalizeSupplier(prod.supplierTitle)

          usageEntry = usageEntries.find(entry => {
            if (!entry.sku) return false

            const entrySku = normalizeSku(entry.sku)
            const entrySupplier = normalizeSupplier(entry.supplierTitle)

            if (!entrySku || !prodSku || entrySku !== prodSku) return false
            // Prefer supplier match when both are known; if one side is missing, accept SKU match.
            return isSupplierCompatible(entrySupplier, prodSupplier)
          })
        }

        // Strategy 3: Fallback by title when SKU matching is unavailable/inconsistent
        if (!usageEntry) {
          const prodTitle = normalizeTitle(prod.title)
          if (prodTitle) {
            usageEntry = usageEntries.find(entry => {
              const entryTitle = normalizeTitle(entry.title)
              if (!entryTitle || entryTitle !== prodTitle) return false
              const entrySupplier = normalizeSupplier(entry.supplierTitle)
              const prodSupplier = normalizeSupplier(prod.supplierTitle)
              return isSupplierCompatible(entrySupplier, prodSupplier)
            })
          }
        }

        if (!prod?.isLinkedStockGroup) {
          if (!usageEntry) return prod
          const usageKey = createMatchKey(usageEntry)
          if (matchedEntryKeys.has(usageKey)) return prod
          usageEntriesForProduct = [usageEntry]
        }
        if (!usageEntriesForProduct.length) return prod

        usageEntriesForProduct.forEach(entry => {
          matchedEntryKeys.add(createMatchKey(entry))
        })

        const usageQty = usageEntriesForProduct.reduce((sum, entry) => {
          if (prod?.isLinkedStockGroup) {
            return (
              sum + convertUsageToRowUnit(entry.quantity, entry?.UoM, prod?.UoM)
            )
          }
          return sum + (Number(entry.quantity) || 0)
        }, 0)
        const currentSold = Number(prod.sold) || 0

        return {
          ...prod,
          sold: currentSold + usageQty,
          soldEntered: true,
        }
      })

      const missingUsageEntries = usageEntries.filter(entry => {
        const matchKey = createMatchKey(entry)
        return !matchedEntryKeys.has(matchKey)
      })
      const matchedCount = matchedEntryKeys.size
      const skippedCount = missingUsageEntries.length
      const skippedPreview = missingUsageEntries
        .slice(0, 5)
        .map(entry => entry?.title || entry?.sku || 'Unknown ingredient')
        .join(', ')

      const applyMatchedUpdates = () => {
        setProducts(updatedProducts)

        // Ensure updatedProductsArray reflects these updates so Save Changes persists them
        const updatedExisting = updatedProducts.filter(p => p._id)
        setUpdatedProductsArray(prev => {
          const map = {}
          prev.forEach(p => {
            if (p._id) {
              map[p._id] = p
            }
          })
          updatedExisting.forEach(p => {
            if (p._id) {
              map[p._id] = p
            }
          })
          return Object.values(map)
        })
      }

      if (isPartialStockTake && matchedCount === 0) {
        notification.warning({
          message: 'No ingredients applied',
          description:
            'None of the selected recipe ingredients match this partial count group, so nothing was changed.',
          placement: 'topRight',
          duration: 6,
        })
        setAddSoldRecipesModalVisible(false)
        return
      }

      if (missingUsageEntries.length > 0) {
        if (isPartialStockTake) {
          const showSkippedWarning = () =>
            notification.warning({
              message: 'Some ingredients were skipped',
              description: `${matchedCount} ingredient(s) applied. ${skippedCount} ingredient(s) skipped because they are outside this partial count group.`,
              placement: 'topRight',
              duration: 6,
            })

          if (skippedCount > 10) {
            Modal.confirm({
              title: 'Many ingredients are outside this partial group',
              content: `${matchedCount} ingredient(s) will apply and ${skippedCount} will be skipped. Continue?`,
              okText: 'Apply matched ingredients',
              cancelText: 'Cancel',
              onOk: () => {
                applyMatchedUpdates()
                showSkippedWarning()
                setAddSoldRecipesModalVisible(false)
              },
            })
            return
          }
          applyMatchedUpdates()
          showSkippedWarning()
          if (skippedPreview) {
            notification.info({
              message: 'Skipped ingredients preview',
              description:
                skippedCount > 5
                  ? `${skippedPreview} and ${skippedCount - 5} more`
                  : skippedPreview,
              placement: 'topRight',
              duration: 5,
            })
          }
        } else {
          applyMatchedUpdates()
          const newProductsFromUsage = missingUsageEntries.map(entry => ({
            title: entry.title || entry.sku || 'Unknown ingredient',
            type: entry.type || '',
            size: entry.size || '',
            UoM: entry.UoM || 'Unit',
            supplierTitle: entry.supplierTitle || 'Unknown supplier',
            // entry.price is typically in minor units (e.g. pennies), so normalise to display price
            price: (Number(entry.price) || 0) / 100,
            OMSPrice: (Number(entry.price) || 0) / 100,
            sku: entry.sku || '',
            lastQuantity: 0,
            inStock: 0,
            wastage: 0,
            sold: Number(entry.quantity) || 0,
            hasVat: entry.hasVat || false,
            stockTake: stockTakeId,
            qtySinceLastStockTake: 0,
          }))

          if (newProductsFromUsage.length > 0) {
            setProducts(prev => [...prev, ...newProductsFromUsage])
            setAddedProductsArray(prev => [...prev, ...newProductsFromUsage])

            SuccessNotification({
              message: 'Missing ingredients added',
              description:
                'We added some missing ingredients to your stock list so your usage stays accurate.',
            })
          }
        }
      } else {
        applyMatchedUpdates()
      }

      setAddSoldRecipesModalVisible(false)
    } catch (error) {
      console.error('Error applying sold recipes:', error)
      notification.error({
        message: 'Sold Recipes failed',
        description:
          'We could not apply Sold Recipes due to an unexpected error. Please try again or contact support if the problem persists.',
        placement: 'topRight',
      })
      setAddSoldRecipesModalVisible(false)
    }
  }

  const handleSaveWastedRecipes = (wastedRecipesData, reason = '') => {
    const businessDate = wastageBusinessDateForStockTake(
      stockTake,
      wastageLondonToday(),
    )
    setSavingWastedRecipes(true)
    let saved = false
    return recordWastedRecipes({
      venueId,
      rows: wastedRecipesData,
      reason,
      businessDate,
    })
      .then(result => {
        if (!result.ok && result.error === 'empty') {
          notification.info({
            message: 'No wasted quantities entered',
            description:
              'No positive wasted quantities were entered, so nothing was recorded.',
            placement: 'topRight',
            duration: 4,
          })
          setAddWastedRecipesModalVisible(false)
          return null
        }
        if (!result.ok) {
          notification.info({
            message: 'Choose a reason',
            description: 'Choose a reason before recording these dishes.',
            placement: 'topRight',
            duration: 4,
          })
          return null
        }
        setWastedRecipeUsage([])
        setUpdatedProductsArray([])
        saved = true
        return dispatch(fetchStockTakeProducts(venueId, stockTakeId))
      })
      .catch(error => {
        console.error('Error recording wasted recipes:', error)
        notification.error({
          message: 'Wasted Recipes failed',
          description:
            error?.message ||
            'We could not record these dishes. Please try again.',
          placement: 'topRight',
        })
        saved = false
        return dispatch(fetchStockTakeProducts(venueId, stockTakeId))
      })
      .then(() => {
        if (!saved) return null
        const today = wastageLondonToday()
        const when =
          businessDate === today
            ? 'today'
            : wastageEntryDateLabel(businessDate, today)
        SuccessNotification({
          message: 'Waste recorded',
          description: `Those dishes are on the Waste list for ${when}. They land on the ingredient lines of this count.`,
        })
        setAddWastedRecipesModalVisible(false)
        return null
      })
      .finally(() => {
        setSavingWastedRecipes(false)
      })
  }

  const onChangeStock = (updatedProduct, value, type) => {
    if (
      type === 'wastage' &&
      stockWastageIsFromTracker(updatedProduct, {
        isPartial: isPartialStockTake,
      })
    ) {
      return
    }
    if (isStockTakeLocked) {
      notification.info({
        message: 'Stock take is locked',
        description: 'This stock take is locked and view-only.',
        placement: 'topRight',
      })
      return
    }
    const nonNumericTextFields = new Set([
      'wastageComment',
      'conversionId',
      'conversionName',
      'conversionUnit',
      'conversionBaseUnit',
      'conversionAppliedAt',
    ])
    const isCommentField = nonNumericTextFields.has(type)

    // Validate input value
    if (value === null || value === undefined) {
      if (type === 'conversionId') {
        // allow clearing conversion selection
      } else {
        return
      }
    }
    if (
      type === 'conversionId' &&
      value !== null &&
      value !== undefined &&
      String(value).trim() === ''
    ) {
      return
    }
    if (!isCommentField) {
      const numValue = Number(value)
      if (isNaN(numValue) || !isFinite(numValue) || numValue < 0) {
        notification.warning({
          message: 'Invalid input',
          description:
            'Please enter a valid number greater than or equal to 0.',
          placement: 'topRight',
          duration: 3,
        })
        return
      }
    }

    const applyFieldUpdate = productToUpdate => {
      if (type === 'wastage') {
        return {
          ...productToUpdate,
          wastage: value,
          liveWastageManaged: false,
          liveWastageDishes: [],
        }
      }
      if (type === 'sold') return { ...productToUpdate, sold: value }
      if (type === 'wastageComment')
        return { ...productToUpdate, wastageComment: value }
      if (type === 'conversionId')
        return { ...productToUpdate, conversionId: value || null }
      if (type === 'conversionName')
        return { ...productToUpdate, conversionName: value || '' }
      if (type === 'conversionUnit')
        return { ...productToUpdate, conversionUnit: value || '' }
      if (type === 'conversionBaseUnit')
        return { ...productToUpdate, conversionBaseUnit: value || '' }
      if (type === 'conversionAppliedAt')
        return { ...productToUpdate, conversionAppliedAt: value || '' }
      if (type === 'conversionFactor')
        return {
          ...productToUpdate,
          conversionFactor: Number.isFinite(Number(value)) ? Number(value) : 1,
        }
      if (type === 'conversionInputQty')
        return {
          ...productToUpdate,
          conversionInputQty: Number.isFinite(Number(value))
            ? Number(value)
            : 0,
        }
      return { ...productToUpdate, inStock: value }
    }

    if (updatedProduct._id) {
      let nextUpdatedProduct = null
      setProducts(prevProducts =>
        prevProducts.map(prod => {
          if (prod._id !== updatedProduct._id) return prod
          nextUpdatedProduct = applyFieldUpdate(prod)
          return nextUpdatedProduct
        }),
      )
      setUpdatedProductsArray(prevUpdated => {
        const existingIndex = prevUpdated.findIndex(
          prod => prod._id === updatedProduct._id,
        )
        if (existingIndex >= 0) {
          return prevUpdated.map((prod, index) =>
            index === existingIndex ? applyFieldUpdate(prod) : prod,
          )
        }
        if (!nextUpdatedProduct) return prevUpdated
        return [...prevUpdated, nextUpdatedProduct]
      })
    } else {
      setAddedProductsArray(
        addedProductsArray.map(prod => {
          if (prod.title === updatedProduct.title) {
            setProducts(
              products.map(prod => {
                if (prod.title === updatedProduct.title) {
                  return applyFieldUpdate(prod)
                }
                return prod
              }),
            )
            return applyFieldUpdate(prod)
          }
          return prod
        }),
      )
    }
  }

  const handleSaveChanges = async () => {
    if (isStockTakeLocked) {
      notification.info({
        message: 'Stock take is locked',
        description: 'This stock take is locked and view-only.',
        placement: 'topRight',
      })
      return false
    }
    const parSavePlan = collectStockTakeParSaves({
      parEdits,
      venueProducts,
      plan: currentPlan,
      venueId,
    })
    if (parSavePlan.blocked) {
      trackParLevelPaywallTriggered({
        venueId,
        source: 'client',
        plan: currentPlan,
      })
      notifyParLevelPaywallSales({
        venueId,
        source: 'client',
        platform: 'dashboard',
        venueProductId: parSavePlan.product?._id ?? null,
        productTitle: parSavePlan.product?.title ?? null,
      })
      Modal.confirm({
        title: 'Upgrade required',
        zIndex: 1100,
        content: <ParLevelUpgradeModalContent />,
        okText: 'Upgrade',
        cancelText: 'Not now',
        onOk: () => history.push(`/v/${venueId}/subscriptions`),
      })
      return false
    }
    let countsPersisted = false
    const persistedParIds = []
    const refreshVenueProductsAndDropPersistedPars = async () => {
      await dispatch(fetchVenueProducts(venueId))
      setParEdits(prev => dropPersistedParEditKeys(prev, persistedParIds))
    }
    try {
      // Validate products before saving
      const validToAdd = addedProductsArray.filter(product => {
        if (!product.title || product.title.trim() === '') {
          console.warn('Skipping product without title:', product)
          return false
        }
        // Validate numeric fields
        const numericFields = [
          'sold',
          'inStock',
          'wastage',
          'price',
          'lastQuantity',
          'qtySinceLastStockTake',
        ]
        for (const field of numericFields) {
          const value = product[field]
          if (value !== undefined && value !== null) {
            const numValue = Number(value)
            if (isNaN(numValue) || !isFinite(numValue) || numValue < 0) {
              console.warn(
                `Invalid ${field} value for product ${product.title}:`,
                value,
              )
              product[field] = 0 // Default to 0 for invalid values
            }
          }
        }
        return true
      })

      const validToUpdate = updatedProductsArray.filter(product => {
        if (!product._id) {
          console.warn('Skipping update without _id:', product)
          return false
        }
        const savedLine = (
          Array.isArray(stockTakeProducts) ? stockTakeProducts : []
        ).find(line => String(line?._id) === String(product._id))
        if (savedLine && !stockTakeLineDiffersFromSaved(savedLine, product)) {
          return false
        }
        // Validate numeric fields
        const numericFields = [
          'sold',
          'inStock',
          'wastage',
          'price',
          'lastQuantity',
          'qtySinceLastStockTake',
        ]
        for (const field of numericFields) {
          const value = product[field]
          if (value !== undefined && value !== null) {
            const numValue = Number(value)
            if (isNaN(numValue) || !isFinite(numValue) || numValue < 0) {
              console.warn(
                `Invalid ${field} value for product ${product._id}:`,
                value,
              )
              product[field] = product[field] || 0 // Default to 0 or existing value
            }
          }
        }
        return true
      })

      const allChanges = {
        stockTakeId: stockTakeId,
        toAdd: validToAdd,
        toUpdate: validToUpdate,
        metadata: stockTakeMetadata,
        soldRecipes,
        wastedRecipes,
        wastedRecipeUsage,
      }
      const saveResult = await dispatch(
        saveAllChanges(venueId, allChanges, 'SAVE_ALL'),
      )
      if (saveResult === false) {
        throw new Error('save_all_changes_failed')
      }
      countsPersisted = validToAdd.length > 0 || validToUpdate.length > 0
      setSavedThisVisit(true)
      await dispatch(
        updateStockTake(
          venueId,
          {
            _id: stockTakeId,
            ...stockTakeMetadata,
          },
          'SAVE_METADATA',
        ),
      )
      setAddedProductsArray([])
      setUpdatedProductsArray([])
      for (const save of parSavePlan.saves) {
        const result = await dispatch(editVenueProduct(save.payload))
        if (result === PAR_LEVEL_LIMIT_ERROR_CODE) {
          await refreshVenueProductsAndDropPersistedPars()
          if (countsPersisted) {
            notification.error({
              message: 'Save failed',
              description: STOCK_TAKE_COUNTS_SAVED_PARS_FAILED,
              placement: 'topRight',
            })
          }
          trackParLevelPaywallTriggered({
            venueId,
            source: 'server',
            plan: currentPlan,
          })
          notifyParLevelPaywallSales({
            venueId,
            source: 'server',
            platform: 'dashboard',
            venueProductId: save.product?._id ?? null,
            productTitle: save.product?.title ?? null,
          })
          Modal.confirm({
            title: 'Upgrade required',
            zIndex: 1100,
            content: <ParLevelUpgradeModalContent />,
            okText: 'Upgrade',
            cancelText: 'Not now',
            onOk: () => history.push(`/v/${venueId}/subscriptions`),
          })
          return false
        }
        if (!result) {
          await refreshVenueProductsAndDropPersistedPars()
          notification.error({
            message: 'Save failed',
            description: countsPersisted
              ? STOCK_TAKE_COUNTS_SAVED_PARS_FAILED
              : 'An error occurred while saving changes. Please try again.',
            placement: 'topRight',
          })
          return false
        }
        persistedParIds.push(String(save.product._id))
        trackParLevelSaveIfChanged({
          venueId,
          venueProductId: save.product._id,
          previousParLevel: save.previousParLevel,
          newParLevel: save.newParLevel,
        })
      }
      setParEdits({})
      setLastSavedAt(new Date().toISOString())
      loadOrderToParReview()
      SuccessNotification({
        message: 'Changes saved',
        description: 'All changes have been saved successfully.',
      })
      return true
    } catch (error) {
      console.error('Error saving changes:', error)
      if (countsPersisted || persistedParIds.length) {
        await refreshVenueProductsAndDropPersistedPars()
      }
      notification.error({
        message: 'Save failed',
        description: countsPersisted
          ? STOCK_TAKE_COUNTS_SAVED_PARS_FAILED
          : 'An error occurred while saving changes. Please try again.',
        placement: 'topRight',
      })
      return false
    }
  }

  const handleLockStatus = async () => {
    if (!stockTake) return
    if (!isStockTakeLocked && hasUnsavedChanges) {
      const saved = await handleSaveChanges()
      if (!saved) return
    }
    confirmStockTakeLockChange({
      stockTake,
      stats: buildReviewBeforeLockStats(stockTake, products),
      venueId,
      venue,
      canUnlockStockTake,
      showVarianceEmail,
      dispatch,
      updateStockTake,
    })
  }

  const handleAddProduct = () => {
    if (isStockTakeLocked) {
      notification.info({
        message: 'Stock take is locked',
        description: 'This stock take is locked and view-only.',
        placement: 'topRight',
      })
      return false
    }

    if (product.recipeId) {
      const recipeId = getRecipeIdString(product.recipeId)
      if (!recipeId) {
        notification.error({
          message: 'Select a recipe',
          description: 'Choose a recipe from the list before adding.',
          placement: 'topRight',
        })
        return false
      }
      const duplicateRecipe = products.some(
        existingProduct =>
          getRecipeIdString(existingProduct.recipeId) === recipeId,
      )
      if (duplicateRecipe) {
        notification.error({
          message: 'Recipe already in stock take',
          description: 'This recipe is already included in this stock take.',
          placement: 'topRight',
        })
        return false
      }
    }

    const isFullFilled =
      product.title.trim() !== '' &&
      product.supplierTitle.trim() !== '' &&
      product.price > 0
    if (isFullFilled) {
      setAddedProductsArray([...addedProductsArray, product])
      setProducts([...products, product])
      setProduct({
        inStock: 0,
        type: '',
        title: '',
        size: '',
        UoM: '',
        sku: '',
        supplierTitle: '',
        price: 0,
        lastQuantity: 0,
        OMSPrice: 0,
        wastage: 0,
        sold: 0,
        hasVat: false,
        stockTake: stockTakeId,
        qtySinceLastStockTake: 0,
        recipeId: null,
        recipePortionsPerBatch: null,
        recipePriceMode: 'Portion',
      })
      setAddProductResetCount(count => count + 1)
      SuccessNotification({
        message: 'Product added',
        description: 'Add another product or close when finished.',
      })
      return true
    }

    notification.error({
      message: 'Fill required fields',
      description: product.recipeId
        ? 'Select a recipe with costing in Menu Builder, or enter a price greater than 0.'
        : 'Title, Supplier title and price must be fulfilled.',
      placement: 'RadiusBottomrightOutlined',
      bottom: 400,
    })
    return false
  }

  const handleUnlinkLinkedGroup = linkedGroupId => {
    if (isStockTakeLocked) {
      notification.info({
        message: 'Stock take is locked',
        description: 'This stock take is locked and view-only.',
        placement: 'topRight',
      })
      return
    }
    if (!linkedGroupId) return

    Modal.confirm({
      title: 'Unlink linked products?',
      content:
        'This will remove the linked group and show products as separate lines again.',
      okText: 'Unlink',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await API.del(
            'api2',
            `venue/${venueId}/stocks/linked-groups/${linkedGroupId}`,
          )
          dispatch(fetchStockTakeProducts(venueId, stockTakeId))
          SuccessNotification({
            message: 'Products unlinked',
            description: 'Linked products were successfully unlinked.',
          })
        } catch (error) {
          console.error('Error unlinking linked group:', error)
          notification.error({
            message: 'Unlink failed',
            description:
              'We could not unlink this product group right now. Please try again.',
            placement: 'topRight',
          })
        }
      },
    })
  }

  const handleOpenAddProductModal = () => {
    if (isStockTakeLocked) {
      notification.info({
        message: 'Stock take is locked',
        description: 'This stock take is locked and view-only.',
        placement: 'topRight',
      })
      return
    }
    setAddProductModalVisible(true)
  }

  const handleChangeOrderToParQty = (item, nextQty) => {
    if (isStockTakeLocked) return
    const key = orderToParSuggestionKey(item)
    if (!key) return
    setOrderToParQtyByKey(prev => ({
      ...prev,
      [key]: Math.max(0, Number(nextQty) || 0),
    }))
  }

  const handleRemoveOrderToPar = item => {
    if (isStockTakeLocked) return
    const key = orderToParSuggestionKey(item)
    if (!key) return
    setOrderToParRemovedKeys(prev =>
      prev.includes(key) ? prev : [...prev, key],
    )
  }

  const handleAddOrderToParToBasket = async () => {
    if (isStockTakeLocked || addingOrderToParToBasket || hasUnsavedChanges)
      return
    setAddingOrderToParToBasket(true)
    try {
      const result = await dispatch(
        addOrderToParToBasket(
          venueId,
          stockTakeId,
          buildOrderToParBasketReviewLines(orderToParReviewRows),
        ),
      )
      if (!result) {
        notification.error({
          message: 'Could not add to basket',
          description: 'Please try again.',
          placement: 'topRight',
        })
        return
      }
      if (result.limitedMessage) {
        notification.warning({
          message: 'Some lines were limited',
          description: result.limitedMessage,
          placement: 'topRight',
        })
      }
      const addedCount = Array.isArray(result.added) ? result.added.length : 0
      if (addedCount) {
        SuccessNotification({ message: ORDER_TO_PAR_ADDED_TO_BASKET })
      } else {
        notification.info({
          message: ORDER_TO_PAR_ALREADY_IN_BASKET,
          placement: 'topRight',
        })
      }
      await loadOrderToParReview()
    } catch (error) {
      console.error(error)
      notification.error({
        message: 'Could not add to basket',
        description: 'Please try again.',
        placement: 'topRight',
      })
    } finally {
      setAddingOrderToParToBasket(false)
    }
  }

  const handleOpenOrderToParReview = () => {
    document
      .getElementById('stock-take-order-to-par-review')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const handleSetParOnCountedLine = () => {
    trackStockTakeAnalytics(venueId, {
      eventName: STOCK_TAKE_ANALYTICS_EVENTS.EMPTY_STATE_CTA_CLICKED,
      platform: 'dashboard',
      stockTakeId,
      metadata: {
        cta: 'set_par',
        emptyState: orderToParEmptyState,
      },
    })
    const line = pickFirstCountedLineWithoutPar({
      skipped: orderToParSkipped,
      products,
    })
    if (!line) return
    const lineId = String(line._id || line.lineId || '').trim()
    setBelowParOnly(false)
    if (lineId) setParFocusLineId(lineId)
    focusStockTakeParInput(line)
  }

  const handleAddMissingParProducts = async () => {
    if (isStockTakeLocked || !missingParProducts.length) return
    setAddingMissingParLines(true)
    try {
      const createdLines = []
      for (const venueProduct of missingParProducts) {
        const created = await dispatch(
          addVenueProductLineToStockTake(
            venueId,
            stockTakeId,
            venueProduct._id,
          ),
        )
        if (created) createdLines.push(created)
      }
      if (createdLines.length) {
        setProducts(prev => [...prev, ...createdLines])
        SuccessNotification({
          message:
            createdLines.length === 1
              ? 'Added uncounted'
              : `Added ${createdLines.length} uncounted`,
          description:
            'They are on this count at 0, not counted, until you enter a figure.',
        })
      }
      if (createdLines.length < missingParProducts.length) {
        notification.warning({
          message: 'Some products were not added',
          description: 'Please try again for any that are still on the strip.',
          placement: 'topRight',
        })
      }
    } finally {
      setAddingMissingParLines(false)
    }
  }

  const moreActionsMenu = (
    <Menu onClick={() => setMoreMenuOpen(false)}>
      <Menu.Item
        key='lock-stock-take'
        onClick={handleLockStatus}
        data-testid='stock-take-lock'
      >
        {isStockTakeLocked ? 'Unlock stock take' : 'Lock stock take'}
      </Menu.Item>
      <Menu.Item
        key='add-missing-product'
        disabled={isStockTakeLocked}
        onClick={handleOpenAddProductModal}
      >
        Add missing product
      </Menu.Item>
      <Menu.Item
        key='download-csv'
        onClick={() => {
          const link = csvMenuLinkRef.current && csvMenuLinkRef.current.link
          if (link) link.click()
        }}
      >
        Download CSV
        <StockTakeCSV
          asMenuItem
          linkRef={csvMenuLinkRef}
          stockTake={stockTake}
          stockTakeProducts={stockTakeProducts}
        />
      </Menu.Item>
      {defaultsToCountMode ? (
        <Menu.Item
          key='count-mode-toggle'
          data-testid='stock-take-count-mode-toggle'
          onClick={() => setShowFullCountGrid(open => !open)}
        >
          {useCountMode ? 'Sold & wastage' : 'Count only'}
        </Menu.Item>
      ) : null}
      {otherPacksToggleLabel ? (
        <Menu.Item
          key='other-packs-toggle'
          data-testid='stock-take-other-packs-toggle'
          onClick={() => setShowOtherPacks(open => !open)}
        >
          {otherPacksToggleLabel}
        </Menu.Item>
      ) : null}
      <Menu.Item
        key='add-sold-recipes'
        disabled={isStockTakeLocked}
        onClick={handleOpenSoldRecipesModal}
        title='Fill Sold from Menu Builder recipes you sold this period.'
      >
        Add Sold Recipes
      </Menu.Item>
      <Menu.Item
        key='add-wasted-recipes'
        disabled={isStockTakeLocked}
        onClick={handleOpenWastedRecipesModal}
        title={WASTAGE_COPY.wastedRecipesSameList}
      >
        <WastedRecipesMenuLabel />
      </Menu.Item>
      <Menu.Item
        key='upload-csv'
        disabled={isStockTakeLocked}
        onClick={() => _file.current?.click()}
        title='Upload a CSV or Excel file (.csv, .xlsx, .xls), or download the template first.'
      >
        Upload file
      </Menu.Item>
      {useCountMode ? (
        <Menu.Item
          key='manage-conversions'
          data-testid='stock-take-more-manage-conversions'
          disabled={isStockTakeLocked}
          onClick={() => setShowFullCountGrid(true)}
        >
          Manage conversions
        </Menu.Item>
      ) : null}
    </Menu>
  )

  const leaveStockTakesPath = `/v/${venueId}/stockTakes`
  const takeHeading = getStockTakePageHeading({
    ...stockTake,
    products: stockTakeProducts,
    partialGroupName:
      stockTake?.partialGroupName ||
      stockTake?.partialGroup?.name ||
      partialGroupLabel,
  })
  const takeDateRange = formatStockTakeDateRange(
    stockTake?.fromPeriod,
    stockTake?.toPeriod,
  )
  const promptLeaveStockTake = shouldPromptStockTakeLeave({
    hasUnsavedChanges,
    isLocked: isStockTakeLocked,
  })

  return (
    <div>
      <Prompt
        when={promptLeaveStockTake}
        message={STOCK_TAKE_UNSAVED_LEAVE_MESSAGE}
      />
      {isLoading || detailState === 'loading' ? (
        <Loading />
      ) : detailState === 'not_found' ? (
        <div data-testid='stock-take-not-found'>
          <Button
            data-testid='stock-take-go-back'
            onClick={() => history.push(leaveStockTakesPath)}
          >
            Go back
          </Button>
          <h1>{STOCK_TAKE_NOT_FOUND_COPY.title}</h1>
          <p>{STOCK_TAKE_NOT_FOUND_COPY.body}</p>
        </div>
      ) : (
        <div>
          <Button
            data-testid='stock-take-go-back'
            onClick={() => history.push(leaveStockTakesPath)}
          >
            Go back
          </Button>
          <h1>
            {takeHeading}
            {takeDateRange ? <StyledSpan>{takeDateRange}</StyledSpan> : null}
          </h1>
          <StatusRow>
            <SaveStatusPill isUnsaved={hasUnsavedChanges}>
              {stockTakeSavePillLabel({
                hasUnsavedChanges,
                lastSavedAt,
                hasPersistedCount,
              })}
            </SaveStatusPill>
            <ProgressText data-testid='stock-take-count-progress'>
              {countProgress.text}
            </ProgressText>
            <BelowParSoFar data-testid='stock-take-below-par-so-far'>
              <BelowParSoFarTotal>{belowParSoFar.text}</BelowParSoFarTotal>
              <BelowParSoFarHint>{belowParSoFar.hint}</BelowParSoFarHint>
            </BelowParSoFar>
            {parLevelUsageText ? (
              <BelowParSoFarHint data-testid='stock-take-par-usage'>
                {parLevelUsageText}
              </BelowParSoFarHint>
            ) : null}
          </StatusRow>
          {orderToParLoaded && orderToParStripCopy ? (
            <OrderToParCompletionStrip data-testid='stock-take-order-to-par-strip'>
              <span data-testid='stock-take-order-to-par-strip-copy'>
                {orderToParStripCopy}
              </span>
              {orderToParEmptyState ? (
                orderToParShowsSetParCta(orderToParEmptyState) &&
                !isStockTakeLocked ? (
                  <CustomButton
                    type='button'
                    data-testid='stock-take-order-to-par-strip-set-par'
                    onMouseDown={event => event.preventDefault()}
                    onClick={handleSetParOnCountedLine}
                  >
                    {ORDER_TO_PAR_SET_PAR_CTA}
                  </CustomButton>
                ) : null
              ) : (
                <CustomButton
                  type='button'
                  data-testid='stock-take-order-to-par-strip-review'
                  onClick={handleOpenOrderToParReview}
                >
                  {ORDER_TO_PAR_STRIP_REVIEW}
                </CustomButton>
              )}
            </OrderToParCompletionStrip>
          ) : null}
          <AuditMeta>
            <span>
              Last edited by: <strong>{lastEditedByText}</strong>
            </span>
            <span>
              Last saved at:{' '}
              <strong>
                {displayedSavedAt
                  ? dayjs(displayedSavedAt).format('DD MMM YYYY, HH:mm')
                  : stockTakeLastSavedLabel({
                      lastSavedAt,
                      hasPersistedCount,
                    })}
              </strong>
            </span>
            {showLockTime ? (
              <span>
                Lock timestamp:{' '}
                <strong>
                  {stockTakeLockLabel({
                    isLocked: true,
                    lockTimestamp,
                  })}
                </strong>
              </span>
            ) : null}
          </AuditMeta>
          {takeMetaRows.length ? (
            <AuditMeta data-testid='stock-take-optional-meta'>
              {takeMetaRows.map(([label, value]) => (
                <span key={label}>
                  {label}: <strong>{value}</strong>
                </span>
              ))}
            </AuditMeta>
          ) : null}
          {isStockTakeLocked && (
            <LockedNotice>
              This stock take is locked and view-only.
            </LockedNotice>
          )}
          {missingParStripCopy && !isStockTakeLocked ? (
            <MissingParStrip data-testid='stock-take-missing-par-strip'>
              <span>{missingParStripCopy}</span>
              <CustomButton
                type='button'
                data-testid='stock-take-add-missing-par'
                onClick={handleAddMissingParProducts}
                disabled={addingMissingParLines}
              >
                {addingMissingParLines && <LoadingOutlined />}
                Add uncounted
              </CustomButton>
            </MissingParStrip>
          ) : null}
          {hasAnyConvertedRows && (
            <ConversionHelperText>
              Some rows are using converted counting units. For those rows, In
              Stock
              {canViewExpectedAndVariance
                ? ', Expected Qty, and Variance are shown in the selected conversion unit.'
                : ' is shown in the selected conversion unit.'}
            </ConversionHelperText>
          )}

          <HeaderRow data-testid='stock-take-count-toolbar'>
            <SaveTakeButton
              type='button'
              data-testid='stock-take-save'
              data-chrome={canSaveTake ? undefined : 'quiet'}
              onClick={handleSaveChanges}
              disabled={
                !canSaveTake ||
                (buttonLoading.isLoading &&
                  buttonLoading.buttonType === 'SAVE_ALL')
              }
            >
              {buttonLoading.isLoading &&
                buttonLoading.buttonType === 'SAVE_ALL' && <LoadingOutlined />}
              Save changes
            </SaveTakeButton>
            <Dropdown
              overlay={moreActionsMenu}
              trigger={['click']}
              visible={moreMenuOpen}
              onVisibleChange={setMoreMenuOpen}
            >
              <StockQuietButton
                type='button'
                data-testid='stock-take-more-actions'
                data-chrome='quiet'
                disabled={
                  buttonLoading.isLoading &&
                  buttonLoading.buttonType === 'UPLOAD_FILE'
                }
              >
                {buttonLoading.isLoading &&
                  buttonLoading.buttonType === 'UPLOAD_FILE' && (
                    <LoadingOutlined />
                  )}
                More
                <DownOutlined style={{ fontSize: 10 }} />
              </StockQuietButton>
            </Dropdown>
            <StockTakeAskOllie
              toolbar
              loaded={orderToParLoaded}
              afterSavedCount={askOllieAfterSavedCount}
              askOllie={orderToParAskOllieChip || orderToParAskOllie}
              onOpen={commitStockTakeInProgressCells}
            />
            <StockQuietButton
              type='button'
              data-testid='stock-take-below-par-filter'
              data-chrome='quiet'
              aria-pressed={belowParOnly}
              onClick={() => setBelowParOnly(on => !on)}
            >
              {STOCK_TAKE_BELOW_PAR_FILTER_LABEL}
              {belowParSoFar.belowParLines
                ? ` (${belowParSoFar.belowParLines})`
                : ''}
            </StockQuietButton>
            <input
              style={{ display: 'none' }}
              onChange={uploadStockTakeFile}
              accept={STOCK_TAKE_FILE_ACCEPT}
              ref={_file}
              type='file'
            />
          </HeaderRow>
          {belowParOnly && displayedStockTakeProducts.length === 0 ? (
            <HelperText data-testid='stock-take-below-par-empty'>
              {STOCK_TAKE_BELOW_PAR_EMPTY}
            </HelperText>
          ) : null}
          <StyledTable
            pagination={false}
            bordered
            dataSource={displayedStockTakeProducts}
            locale={
              belowParOnly
                ? {
                    emptyText: (
                      <span data-testid='stock-take-below-par-empty-table'>
                        {STOCK_TAKE_BELOW_PAR_EMPTY}
                      </span>
                    ),
                  }
                : undefined
            }
            rowKey={record =>
              record?._id ||
              record?.id ||
              `${record?.title || 'row'}-${
                record?.supplierTitle || 'supplier'
              }-${record?.sku || 'sku'}`
            }
            columns={columns}
            rowClassName={record =>
              [
                parFocusLineId &&
                String(record?._id || record?.lineId || '') === parFocusLineId
                  ? STOCK_TAKE_PAR_FOCUS_ROW_CLASS
                  : '',
                shouldShowDuplicatePackWarning(record)
                  ? 'stock-take-duplicate-pack-row'
                  : '',
              ]
                .filter(Boolean)
                .join(' ')
            }
            tableLayout='fixed'
            scroll={{ x: sumStockTakeColumnScrollX(columns), y: '55vh' }}
            footer={() => {
              const stockTotal = products.reduce((sum, item) => {
                return (
                  sum +
                  (item.hasVat
                    ? item.price * item.inStock * VAT_RATE
                    : item.price * item.inStock)
                )
              }, 0)
              const wastageTotal = products.reduce((sum, item) => {
                return (
                  sum +
                  (item.hasVat
                    ? item.price * item.wastage * VAT_RATE
                    : item.price * item.wastage)
                )
              }, 0)
              return (
                <StockTakeTotals data-testid='stock-take-totals'>
                  <span>{`Stock ${formatGbp(stockTotal)}`}</span>
                  <span>{`Wastage ${formatGbp(wastageTotal)}`}</span>
                </StockTakeTotals>
              )
            }}
          />
          {isStockTakeLocked ? (
            <OrderToParLockMoment data-testid='stock-take-order-to-par-lock'>
              <span>{orderToParLockCopy}</span>
              <SecondaryOutlineButton
                type='button'
                data-testid='stock-take-order-to-par-lock-review'
                onClick={handleOpenOrderToParReview}
              >
                Review remaining
              </SecondaryOutlineButton>
            </OrderToParLockMoment>
          ) : null}
          <HelperText data-testid='stock-take-count-hint'>
            {ORDER_TO_PAR_COUNT_HINT}
          </HelperText>
          {orderToParLoaded || hasUnsavedChanges || isStockTakeLocked ? (
            <OrderToParReview
              id='stock-take-order-to-par-review'
              data-testid='stock-take-order-to-par-review'
            >
              <OrderToParReviewHeader>
                <strong>Order to par</strong>
                {orderToParEmptyState ? null : (
                  <OrderToParReviewHint>
                    {ORDER_TO_PAR_REVIEW_HINT}
                  </OrderToParReviewHint>
                )}
                {hasUnsavedChanges && !orderToParEmptyState ? (
                  <OrderToParReviewHint data-testid='stock-take-order-to-par-save-before-basket'>
                    {ORDER_TO_PAR_SAVE_BEFORE_BASKET}
                  </OrderToParReviewHint>
                ) : null}
                {orderToParEmptyState ? null : (
                  <OrderToParReviewTotal data-testid='stock-take-order-to-par-total'>
                    {orderToParReviewSummary.belowParCount === 1
                      ? '1 product remaining'
                      : `${orderToParReviewSummary.belowParCount} products remaining`}
                    {' · '}
                    {hasUnsavedChanges
                      ? ORDER_TO_PAR_PREVIEW_TOTAL_HINT
                      : `${formatGbp(
                          orderToParReviewSummary.estimatedValue,
                        )} after basket and open orders`}
                  </OrderToParReviewTotal>
                )}
                {inactiveOrderToParSkippedCount > 0 ? (
                  <OrderToParReviewHint data-testid='stock-take-order-to-par-inactive-hint'>
                    {ORDER_TO_PAR_INACTIVE_SKIPPED_HINT}
                  </OrderToParReviewHint>
                ) : null}
              </OrderToParReviewHeader>
              {orderToParEmptyState ? (
                <OrderToParEmpty>
                  <OrderToParReviewHint
                    data-testid='stock-take-order-to-par-empty'
                    data-empty-state={orderToParEmptyState}
                  >
                    {orderToParEmptyMessage}
                  </OrderToParReviewHint>
                  {orderToParShowsSetParCta(orderToParEmptyState) &&
                  !isStockTakeLocked ? (
                    <CustomButton
                      type='button'
                      data-testid='stock-take-set-par-on-line'
                      onMouseDown={event => event.preventDefault()}
                      onClick={handleSetParOnCountedLine}
                    >
                      {ORDER_TO_PAR_SET_PAR_CTA}
                    </CustomButton>
                  ) : null}
                </OrderToParEmpty>
              ) : (
                orderToParReviewRows.map(item => {
                  const key = orderToParSuggestionKey(item)
                  const packCopy = formatOrderToParPackCopy(item)
                  const coverageCopy = formatOrderToParCoverageCopy(item)
                  return (
                    <OrderToParReviewRow
                      key={key}
                      data-testid={`order-to-par-row-${key}`}
                    >
                      <OrderToParReviewCopy>
                        <strong>{item.title}</strong>
                        <span>
                          {item.currentStock} on shelf · par {item.parLevel} ·
                          gap {item.requiredQuantity || item.suggestedOrder}
                          {packCopy ? ` · ${packCopy}` : ''}
                          {coverageCopy ? ` · ${coverageCopy}` : ''}
                        </span>
                      </OrderToParReviewCopy>
                      <InputNumber
                        min={0}
                        value={item.suggestedOrder}
                        disabled={isStockTakeLocked || hasUnsavedChanges}
                        onChange={qty => handleChangeOrderToParQty(item, qty)}
                        aria-label={`Suggested order for ${item.title}`}
                      />
                      <span>{formatGbp(item.estimatedValue || 0)}</span>
                      <SecondaryOutlineButton
                        type='button'
                        data-testid={`order-to-par-remove-${key}`}
                        disabled={isStockTakeLocked || hasUnsavedChanges}
                        onClick={() => handleRemoveOrderToPar(item)}
                      >
                        Remove
                      </SecondaryOutlineButton>
                    </OrderToParReviewRow>
                  )
                })
              )}
              {canAddOrderToParToBasket ? (
                <OrderToParReviewActions>
                  <CustomButton
                    type='button'
                    data-testid='stock-take-order-to-par-add-to-basket'
                    onClick={handleAddOrderToParToBasket}
                    disabled={addingOrderToParToBasket}
                  >
                    {addingOrderToParToBasket && <LoadingOutlined />}
                    {orderToParAddToBasketLabel(orderToParReviewRows)}
                  </CustomButton>
                </OrderToParReviewActions>
              ) : null}
            </OrderToParReview>
          ) : null}
          <Modal
            title='Add Missing Product'
            width='60%'
            visible={addProductModalVisible}
            onOk={handleAddProduct}
            onCancel={() => setAddProductModalVisible(false)}
            okText={'Add'}
            okButtonProps={{ disabled: isStockTakeLocked }}
          >
            <AddProductModal
              product={product}
              setProduct={setProduct}
              venueProducts={[...venueProducts]}
              recipes={recipes}
              addProductModalVisible={addProductModalVisible}
              stockTakeProducts={products}
              onSubmit={handleAddProduct}
              addProductResetCount={addProductResetCount}
              isSubmitDisabled={isStockTakeLocked}
            />
          </Modal>
          <AddSoldRecipesModal
            visible={addSoldRecipesModalVisible}
            onCancel={() => setAddSoldRecipesModalVisible(false)}
            onSave={handleSaveSoldRecipes}
            recipes={recipes}
            existingSoldRecipes={soldRecipes}
            products={products}
            isPartialStockTake={isPartialStockTake}
            partialScopeProductKeys={partialScopeProductKeys}
          />
          <AddWastedRecipesModal
            visible={addWastedRecipesModalVisible}
            onCancel={() => setAddWastedRecipesModalVisible(false)}
            onSave={handleSaveWastedRecipes}
            recipes={recipes}
            products={products}
            isPartialStockTake={isPartialStockTake}
            partialScopeProductKeys={partialScopeProductKeys}
            confirmLoading={savingWastedRecipes}
            recordedOn={
              wastageBusinessDateForStockTake(
                stockTake,
                wastageLondonToday(),
              ) === wastageLondonToday()
                ? 'Recorded today'
                : `Recorded on ${wastageEntryDateLabel(
                    wastageBusinessDateForStockTake(
                      stockTake,
                      wastageLondonToday(),
                    ),
                    wastageLondonToday(),
                  )}`
            }
          />
          <ProductConversionModal
            visible={conversionModalState.visible}
            stockTakeProduct={conversionModalState.product}
            venueId={venueId}
            venueProducts={venueProducts}
            isStockTakeLocked={isStockTakeLocked}
            onClose={handleCloseConversionModal}
            onConversionsUpdated={handleConversionsUpdated}
            onApplyConversion={handleApplyConversionToRow}
          />
        </div>
      )}
    </div>
  )
}

const StockTakeTotals = styled.div`
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-end;
  gap: 2rem;
  font-weight: 700;
  white-space: nowrap;
`

const HeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 10px;
  gap: 10px;
`

const StyledSpan = styled.span`
  margin-left: 5px;
  font-size: 20px;
`

const HelperText = styled.p`
  margin: 8px 0 16px;
  max-width: 720px;
  color: #666;
  line-height: 1.4;
`

const OrderToParReview = styled.section`
  margin: 0 0 14px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #f0f0f0;
  background: #fafafa;
`

const OrderToParReviewHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 8px;
`

const OrderToParReviewHint = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#8c8c8c'};
`

const OrderToParEmpty = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
`

const OrderToParReviewRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  padding: 8px 0;
  border-top: 1px solid #f0f0f0;
`

const OrderToParReviewCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 12rem;
  min-width: 0;
  font-size: 13px;
`

const OrderToParReviewTotal = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colours?.otherBlack || '#262626'};
`

const OrderToParReviewActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
`

const OrderToParLockMoment = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px 16px;
  margin: 0 0 14px;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #f0f0f0;
  background: #fafafa;
  color: ${({ theme }) => theme?.colours?.otherBlack || '#262626'};
  font-size: 13px;
  line-height: 1.4;
`

const OrderToParCompletionStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px 16px;
  margin: 0 0 14px;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #e8f4ff;
  background: #f7fbff;
  color: ${({ theme }) => theme?.colours?.otherBlack || '#262626'};
  font-size: 13px;
  line-height: 1.4;
`

const MissingParStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px 16px;
  margin: 0 0 14px;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #e8f4ff;
  background: #f7fbff;
  color: ${({ theme }) => theme?.colours?.otherBlack || '#262626'};
  font-size: 13px;
  line-height: 1.4;
`

const ConversionHelperText = styled.div`
  margin: -8px 0 14px;
  max-width: 760px;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid #d6e4ff;
  background: #f0f5ff;
  color: #1d39c4;
  font-size: 12px;
  line-height: 1.4;
`

const BelowParSoFar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px 10px;
  margin-left: auto;
`

const BelowParSoFarTotal = styled.strong`
  font-size: 14px;
  color: ${({ theme }) => theme?.colours?.otherBlack || '#262626'};
`

const BelowParSoFarHint = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#8c8c8c'};
`

const StatusRow = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 12px;
  width: fit-content;
  margin: 8px 0 8px;
  padding: 6px 10px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid #f0f0f0;
`

const SaveStatusPill = styled.span`
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 700;
  background: ${({ isUnsaved }) => (isUnsaved ? '#fff1f0' : '#f6ffed')};
  color: ${({ isUnsaved }) => (isUnsaved ? '#cf1322' : '#389e0d')};
  border: 1px solid ${({ isUnsaved }) => (isUnsaved ? '#ffa39e' : '#b7eb8f')};
`

const ProgressText = styled.span`
  font-size: 12px;
  color: #595959;
  font-weight: 600;
`

const AuditMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 12px;
  color: #595959;
  margin: 0 0 10px;
`

const InlineMutedText = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #8c8c8c;
`

const LockedNotice = styled.div`
  margin: 8px 0 12px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #ffa39e;
  background: #fff1f0;
  color: #a8071a;
  font-weight: 600;
`

const headerActionButtonBase = css`
  height: 36px;
  box-sizing: border-box;
  padding: 0 14px;
  border-radius: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-weight: 700;
  font-size: 14px;
  line-height: 1;
  white-space: nowrap;
`

const CustomButton = styled.button`
  ${headerActionButtonBase}
  background-color: ${({ theme }) => theme.colours.lettuceGreen};
  color: ${({ theme }) => theme.colours.mayoWhite};
  border: 2px solid ${({ theme }) => theme.colours.lettuceGreen};
  :hover {
    cursor: pointer;
  }
  :disabled {
    background-color: ${({ theme }) => theme.colours.chilliRed};
    :hover {
      cursor: not-allowed;
    }
  }
`

const SecondaryOutlineButton = styled.button`
  ${headerActionButtonBase}
  background-color: #fff;
  color: ${({ theme }) => theme.colours.bbqBlack};
  border: 2px solid ${({ theme }) => theme.colours.lettuceGreen};
  .anticon {
    color: inherit;
  }
  :hover {
    cursor: pointer;
    background-color: #f6ffed;
  }
  :disabled {
    opacity: 0.55;
    cursor: not-allowed;
    :hover {
      background-color: #fff;
    }
  }
`

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    white-space: nowrap;
  }
  .inputColumn {
    padding: 8px 10px;
  }
  .smallColumn {
    min-width: 10px;
  }
  .uomColumn {
    min-width: 160px;
  }
  .centerAlign {
    text-align: center;
  }
  .ant-input-number-input {
    text-align: center !important;
  }

  ${antdTableHorizontalScroll}
`

const UomCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const UomCellRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 6px;
  align-items: center;
  width: 100%;
`

const UomLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const UomSelect = styled(Select)`
  && {
    width: 100%;
    min-width: 0;
  }
`

const UomEditButton = styled.button`
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.colours.lettuceGreen};
  border-radius: 6px;
  background: #f6ffed;
  color: ${({ theme }) => theme.colours.bbqBlack};
  cursor: pointer;
  padding: 0;

  .anticon {
    font-size: 15px;
    color: inherit;
  }

  &:hover {
    background: #eef9d5;
    border-color: ${({ theme }) => theme.colours.lettuceGreen};
  }
`

export default Stock
